import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode
} from 'react';
import type {
  DashboardPeriod,
  DashboardResponse
} from '../worker/dashboard/types';
import { isBelowSafetyThreshold } from '../worker/dashboard/forecast';
import { readApiError, type ApiErrorMessages } from './api-errors';
import { resolveLocale } from '../worker/users/locale';

type Language = 'ko' | 'ja';
type Country = 'JP' | 'KR';
type Currency = 'JPY' | 'KRW';
type AccountType = 'bank' | 'cash' | 'savings' | 'prepaid' | 'other';
type OwnershipType = 'family' | 'self' | 'spouse' | 'child' | 'shared';
type TransactionType =
  | 'manual_deposit'
  | 'manual_withdrawal'
  | 'manual_transfer';
type RecurringFrequency = 'once' | 'monthly_day';
type AuthState = 'checking' | 'signed-in' | 'signed-out';

type AppTab = 'dashboard' | 'accounts' | 'planned' | 'outflow' | 'settings';

interface AppProps {
  initialAuthState?: AuthState;
  initialSession?: SessionInfo | null;
  initialTab?: AppTab;
}

interface SessionInfo {
  user: { id: string; email: string; preferredLocale?: Language | null };
  workspace: { id: string; defaultLocale?: Language };
  member: { id: string; role: string };
}

interface WorkspaceMemberRecord {
  id: string;
  workspaceId: string;
  email: string;
  role: string;
  status: 'active' | 'disabled';
  displayName: string | null;
  userId: string | null;
}

interface MemberFormState {
  email: string;
  role: 'editor' | 'viewer';
}

interface Account {
  id: string;
  name: string;
  country: Country;
  currency: Currency;
  accountType: AccountType;
  ownershipType: OwnershipType;
  balance: number;
  includeInDashboard: boolean;
  allowNegativeBalance: boolean;
  safetyThreshold: number | null;
  isActive: boolean;
  archivedAt: string | null;
  bankAccountNumber: string | null;
}

interface BankImportPreviewResponse {
  adapterId: 'hokoku' | 'sbi_shinsei';
  meta: {
    bankName: string;
    accountNumber: string | null;
  };
  summary: {
    total: number;
    new: number;
    duplicate: number;
    error: number;
  };
  rowsTruncated?: boolean;
  warnings: Array<{ code: string; message: string }>;
  suggestedInitialBalance: number | null;
  balanceReconcile: {
    statementEndBalance: number | null;
    statementEndDate: string | null;
    currentBalance: number;
    projectedBalanceAfterImport: number;
    reconcileDelta: number;
    reconcileAlreadyApplied: boolean;
    canApplyReconcile: boolean;
  };
}

interface LedgerTransaction {
  id: string;
  type: string;
  currency: Currency;
  localDate: string;
  note: string | null;
  sourceType: string;
  entry: {
    amount: number;
    currency: Currency;
    role: string;
  };
}

interface RecurringExpenseSuggestion {
  id: string;
  label: string;
  amount: number;
  currency: Currency;
  occurrenceCount: number;
  distinctMonthCount: number;
  confidence: number;
  suggestedRecurringRule: {
    frequency: RecurringFrequency;
    startDate: string;
    dayOfMonth: number | null;
  };
  alreadyRegistered: boolean;
}

interface AccountFormState {
  name: string;
  country: Country;
  accountType: AccountType;
  ownershipType: OwnershipType;
  initialBalance: string;
  includeInDashboard: boolean;
  allowNegativeBalance: boolean;
  safetyThreshold: string;
  bankAccountNumber: string;
}

interface TransactionFormState {
  type: TransactionType;
  accountId: string;
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  localDate: string;
  note: string;
}

interface IncomeOccurrenceFormState {
  accountId: string;
  amount: string;
  expectedDate: string;
  note: string;
}

interface RecurringFormState {
  frequency: RecurringFrequency;
  startDate: string;
  dayOfMonth: string;
}

type FixedExpenseLedgerSource = 'bank' | 'schedule';

interface FixedExpenseFormState {
  name: string;
  accountId: string;
  amount: string;
  ledgerSource: FixedExpenseLedgerSource;
  recurring: RecurringFormState;
}

interface ScheduledTransferFormState {
  name: string;
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  recurring: RecurringFormState;
}

interface IncomeOccurrence {
  id: string;
  accountId: string;
  amount: number;
  currency: Currency;
  expectedDate: string;
  status: string;
  note: string | null;
}

interface FixedExpense {
  id: string;
  name: string;
  accountId: string;
  country: Country;
  currency: Currency;
  amount: number;
  ledgerSource: FixedExpenseLedgerSource;
  isActive: boolean;
}

interface ScheduledTransfer {
  id: string;
  name: string;
  fromAccountId: string;
  toAccountId: string;
  amount: number;
  currency: Currency;
  isActive: boolean;
}

type ScheduledOutflowTab = 'cards' | 'loans';
type CardBillingCycleStatus =
  | 'planned'
  | 'paid'
  | 'cancelled'
  | 'adjusted';
type LoanPaymentScheduleStatus =
  | 'planned'
  | 'paid'
  | 'cancelled'
  | 'adjusted';

interface CardRecord {
  id: string;
  name: string;
  country: Country;
  currency: Currency;
  ownershipType: OwnershipType;
  paymentAccountId: string;
  paymentDay: number;
  isActive: boolean;
}

interface CardBillingCycleRecord {
  id: string;
  cardId: string;
  paymentMonth: string;
  statementMonth: string;
  paymentDueDate: string;
  amount: number;
  currency: Currency;
  status: CardBillingCycleStatus;
}

interface LoanRecord {
  id: string;
  name: string;
  country: Country;
  currency: Currency;
  principalAmount: number;
  annualInterestRate: number;
  startDate: string;
  termYears: number;
  paymentDay: number;
  paymentAccountId: string;
  isActive: boolean;
}

interface LoanPaymentScheduleRecord {
  id: string;
  loanId: string;
  paymentNumber: number;
  dueDate: string;
  paymentAmount: number;
  status: LoanPaymentScheduleStatus;
}

interface CardFormState {
  name: string;
  ownershipType: OwnershipType;
  paymentAccountId: string;
  paymentDay: string;
}

interface BillingCycleFormState {
  cardId: string;
  paymentMonth: string;
  paymentDueDate: string;
  amount: string;
  statementMonth: string;
}

interface LoanFormState {
  name: string;
  principalAmount: string;
  annualInterestRate: string;
  startDate: string;
  termYears: string;
  paymentDay: string;
  paymentAccountId: string;
}

const copy = {
  ko: {
    accounts: '\uacc4\uc88c',
    active: '\uc0ac\uc6a9 \uc911',
    activeAccounts: '\ud65c\uc131 \uacc4\uc88c \uc694\uc57d',
    amount: '\uae08\uc561',
    availableBalance: '\uc0ac\uc6a9 \uac00\ub2a5 \uc794\uc561',
    balance: '\uc794\uc561',
    checking: '\uc138\uc158 \ud655\uc778 \uc911',
    selectPlaceholder: '\uc120\ud0dd',
    saved: '\uc800\uc7a5\ub418\uc5c8\uc2b5\ub2c8\ub2e4',
    catchUpDone:
      '\uae30\ud55c\uc774 \uc9c0\ub09c \uc608\uc815 \ud56d\ubaa9\uc744 \uc6d0\uc7a5\uc5d0 \ubc18\uc601\ud588\uc2b5\ub2c8\ub2e4',
    requestFailed: '\uc694\uccad\uc5d0 \uc2e4\ud328\ud588\uc2b5\ub2c8\ub2e4',
    errorGeneric: '\uc694\uccad\uc744 \ucc98\ub9ac\ud558\uc9c0 \ubabb\ud588\uc2b5\ub2c8\ub2e4',
    errorUnauthorized: '\ub85c\uadf8\uc778\uc774 \ud544\uc694\ud569\ub2c8\ub2e4',
    errorForbidden: '\uad8c\ud55c\uc774 \uc5c6\uc2b5\ub2c8\ub2e4',
    errorNetwork: '\ub124\ud2b8\uc6cc\ud06c \uc624\ub958\uac00 \ubc1c\uc0dd\ud588\uc2b5\ub2c8\ub2e4',
    errorConflict: '\uc774\ubbf8 \uc874\uc7ac\ud558\uac70\ub098 \uae08\uc9c0\ub41c \uc791\uc5c5\uc785\ub2c8\ub2e4',
    errorNotFound: '\ub300\uc0c1\uc744 \ucc3e\uc9c0 \ubabb\ud588\uc2b5\ub2c8\ub2e4',
    errorInvalidJson: '\uc694\uccad \ud615\uc2dd\uc774 \uc62c\ubc14\ub974\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4',
    accountTypeBank: '\uc740\ud589',
    accountTypeCash: '\ud604\uae08',
    accountTypeSavings: '\uc800\ucd95',
    accountTypePrepaid: '\uc120\ubd88',
    accountTypeOther: '\uae30\ud0c0',
    ownershipFamily: '\uac00\uc871',
    ownershipSelf: '\ubcf8\uc778',
    ownershipSpouse: '\ubc30\uc6b0\uc790',
    ownershipChild: '\uc790\ub140',
    ownershipShared: '\uacf5\uc720',
    txnDeposit: '\uc785\uae08',
    txnWithdrawal: '\ucd9c\uae08',
    txnTransfer: '\uc774\uccb4',
    countryJp: '\uc77c\ubcf8',
    countryKr: '\ud55c\uad6d',
    belowSafetyThreshold: '\uc548\uc804 \uae30\uc900 \uc774\ud558',
    negativeBalanceWarning: '\ub9c8\uc774\ub108\uc2a4 \uc794\uc561',
    createAccount: '\uacc4\uc88c \ub9cc\ub4e4\uae30',
    currentBalance: '\ud604\uc7ac \uc794\uc561',
    dashboard: '\ub300\uc2dc\ubcf4\ub4dc',
    date: '\ub0a0\uc9dc',
    dashboardOn: '\ub300\uc2dc\ubcf4\ub4dc \ud3ec\ud568',
    dashboardOff: '\ub300\uc2dc\ubcf4\ub4dc \uc81c\uc678',
    eventCount: '\uc774\ubca4\ud2b8',
    forecast: '\uc608\uce21',
    forecastEmpty: '\uc608\uce21 \ud56d\ubaa9\uc774 \uc544\uc9c1 \uc5c6\uc2b5\ub2c8\ub2e4.',
    inflow: '\uc720\uc785',
    loadingDashboard: '\ub300\uc2dc\ubcf4\ub4dc\ub97c \ubd88\ub7ec\uc624\ub294 \uc911',
    lowestExpectedBalance: '\ucd5c\uc800 \uc608\uc0c1 \uc794\uc561',
    emptyAccounts: '\uc544\uc9c1 \uacc4\uc88c\uac00 \uc5c6\uc2b5\ub2c8\ub2e4.',
    familyFinance: '\uac00\uc871 \uc7ac\uc815 \ub300\uc2dc\ubcf4\ub4dc',
    googleLogin: 'Google\ub85c \ub85c\uadf8\uc778',
    history: '\uac70\ub798 \ub0b4\uc5ed',
    inactive: '\ube44\ud65c\uc131',
    loginPrompt:
      '\uac00\uc871 \uc6cc\ud06c\uc2a4\ud398\uc774\uc2a4\uc5d0 \ub85c\uadf8\uc778\ud574 \uacc4\uc88c\uc640 \uc218\ub3d9 \uac70\ub798\ub97c \uad00\ub9ac\ud558\uc138\uc694.',
    name: '\uc774\ub984',
    newAccount: '\uc0c8 \uacc4\uc88c',
    noTransactions:
      '\uc120\ud0dd\ud55c \uacc4\uc88c\uc758 \uac70\ub798\uac00 \uc5c6\uc2b5\ub2c8\ub2e4.',
    note: '\uba54\ubaa8',
    outflow: '\uc720\ucd9c',
    period: '\uae30\uac04',
    periodInflow: '\uae30\uac04 \uc720\uc785',
    periodOutflow: '\uae30\uac04 \uc720\ucd9c',
    plannedCashflow: '\uc608\uc815 \ud604\uae08\ud750\ub984',
    plannedCashflowEmpty:
      '\uc544\uc9c1 \uc608\uc815 \ud604\uae08\ud750\ub984\uc774 \uc5c6\uc2b5\ub2c8\ub2e4.',
    plannedIncome: '\uc608\uc815 \uc218\uc785',
    fixedExpense: '\uace0\uc815 \uc9c0\ucd9c',
    ledgerSource: '\uc2e4\uc801 \ubc18\uc601',
    ledgerSourceBank: '\uc740\ud589 CSV\u00b7\uc218\ub3d9',
    ledgerSourceBankHint:
      '\uc2e4\uc81c \ucd9c\uae08\uc740 \uc740\ud589 CSV\ub85c \ubc18\uc601\ub429\ub2c8\ub2e4. \uc774 \ud56d\ubaa9\uc740 \uc55e\uc73c\ub85c\uc758 \uc608\uc0c1 \uc9c0\ucd9c\uc5d0\ub9cc \uc4f0\uc785\ub2c8\ub2e4.',
    ledgerSourceSchedule: '\uc608\uc815\uc77c \uc790\ub3d9 \uc804\ud45c',
    ledgerSourceScheduleHint:
      'CSV \uc5c6\uc774 \uc608\uc815\uc77c\uc5d0 \uc790\ub3d9\uc73c\ub85c \uac70\ub798\ub97c \ub9cc\ub4ed\ub2c8\ub2e4.',
    scheduledTransfer: '\uc608\uc815 \uc774\uccb4',
    scheduledOutflow: '\uc608\uc815 \uc9c0\ucd9c \uad00\ub9ac',
    scheduledOutflowEmpty:
      '\uc544\uc9c1 \uc608\uc815 \uc9c0\ucd9c\uc774 \uc5c6\uc2b5\ub2c8\ub2e4.',
    cards: '\uce74\ub4dc',
    loans: '\ub300\ucd9c',
    card: '\uce74\ub4dc',
    markBillingPaid: '\uacb0\uc81c \uc644\ub8cc',
    markLoanPaid: '\uc0c1\ud658 \uc644\ub8cc',
    paymentDay: '\uacb0\uc81c\uc77c',
    paymentMonth: '\uccad\uad6c\uc6d4',
    paymentDueDate: '\uacb0\uc81c \uc608\uc815\uc77c',
    statementMonth: '\uba85\uc138\uc6d4',
    principalAmount: '\uc6d0\uae08',
    annualInterestRate: '\uc5f0\uc774\uc728',
    termYears: '\uc0c1\ud658 \uae30\uac04(\ub144)',
    recurringRule: '\ubc18\ubcf5 \uaddc\uce59',
    oneTime: '\uc77c\ud68c\uc131',
    monthlyDay: '\ub9e4\uc6d4 \uc9c0\uc815\uc77c',
    dayOfMonth: '\uc77c',
    startDate: '\uc2dc\uc791\uc77c',
    expectedDate: '\uc608\uc815\uc77c',
    fromAccount: '\ucd9c\uae08 \uacc4\uc88c',
    ownership: '\uc18c\uc720',
    save: '\uc800\uc7a5',
    safetyThreshold: '\uc548\uc804 \uae30\uc900\uc120',
    selectAccount: '\uacc4\uc88c \uc120\ud0dd',
    transactions: '\uac70\ub798',
    transactionType: '\uac70\ub798 \uc720\ud615',
    transferTarget: '\uc774\uccb4 \ub300\uc0c1',
    workspace: '\uc6cc\ud06c\uc2a4\ud398\uc774\uc2a4',
    members: '\uba64\ubc84 \uad00\ub9ac',
    memberEmail: '\uc774\uba54\uc77c',
    memberRole: '\uc5ed\ud560',
    memberStatus: '\uc0c1\ud0dc',
    addMember: '\uba64\ubc84 \ucd94\uac00',
    disableMember: '\ube44\ud65c\uc131\ud654',
    disableFixedExpense: '\uace0\uc815 \uc9c0\ucd9c \ube44\ud65c\uc131\ud654',
    disableFixedExpenseHint:
      '\uc608\uc815 \ubaa9\ub85d\uc5d0\uc11c \uc228\uae41\ub2c8\ub2e4. \uc774\ubbf8 \ubc18\uc601\ub41c \uac70\ub798\ub294 \uc720\uc9c0\ub429\ub2c8\ub2e4.',
    membersEmpty: '\ub4f1\ub85d\ub41c \uba64\ubc84\uac00 \uc5c6\uc2b5\ub2c8\ub2e4.',
    exportWorkspace: '\uc6cc\ud06c\uc2a4\ud398\uc774\uc2a4 JSON\ub0b4\ubcf4\ub0b4\uae30',
    exportAccountCsv: '\uac70\ub798 CSV',
    exportDownload: '\ub2e4\uc6b4\ub85c\ub4dc',
    exportAccountSelect: '\uacc4\uc88c \uc120\ud0dd',
    importBankCsv: '\uac70\ub798\u00b7\uacc4\uc88c CSV \uac00\uc838\uc624\uae30',
    importBankCsvSelect: 'CSV \ud30c\uc77c \uc120\ud0dd',
    importBankPreview: '\ubbf8\ub9ac\ubcf4\uae30',
    importBankCommit: '\uac00\uc838\uc624\uae30',
    importBankApplyInitial: '\uc81c\uc548 \ucd08\uae30\uc794\uc561 \uc801\uc6a9',
    importBankReconcile: '\uba85\uc138 \ub9c8\uc9c0\ub9c9 \uc794\uc561\uc5d0 \ub9de\ucdb0 \uc870\uc815',
    importBankStatementEndBalance: '\uba85\uc138 \ub9c8\uc9c0\ub9c9 \uc794\uc561',
    importBankProjectedBalance: '\uac00\uc838\uc628 \ub4a4 \uc608\uc0c1 \uc794\uc561',
    importBankReconcileDelta: '\uc870\uc815 \ud544\uc694\uc561',
    importBankReconcileDone:
      '\uc774 \uacc4\uc88c\uc5d0\ub294 \uc774\ubbf8 \uc794\uc561 \ub9de\ucdb4\uc774 \uc801\uc6a9\ub428. \uc2e0\uaddc \uac70\ub798\ub9cc \uac00\uc838\uc635\ub2c8\ub2e4.',
    archiveAccount: '\uacc4\uc88c \ubcf4\uad00',
    archiveAccountHint:
      '\ubaa9\ub85d\uc5d0\uc11c \uc228\uae30\uace0 \uac70\ub798 \uae30\ub85d\uc740 \uc720\uc9c0\ud569\ub2c8\ub2e4.',
    archivedAccounts: '\ubcf4\uad00\ub41c \uacc4\uc88c',
    archivedAccountsEmpty: '\ubcf4\uad00\ub41c \uacc4\uc88c\uac00 \uc5c6\uc2b5\ub2c8\ub2e4.',
    showArchivedAccounts: '\ubcf4\uad00 \ubaa9\ub85d \ubcf4\uae30',
    hideArchivedAccounts: '\ubcf4\uad00 \ubaa9\ub85d \uc228\uae30\uae30',
    recurringSuggestions: '\ubc18\ubcf5 \uc9c0\ucd9c \uc81c\uc548',
    recurringSuggestionsEmpty:
      '\uc774 \uacc4\uc88c\uc5d0\uc11c \ubc18\ubcf5 \ud328\ud134\uc774 \uc544\uc9c1 \uc5c6\uc2b5\ub2c8\ub2e4.',
    showRecurringSuggestions: '\uc81c\uc548 \ubcf4\uae30',
    hideRecurringSuggestions: '\uc81c\uc548 \uc228\uae30\uae30',
    recurringSuggestionConfidence: '\uc2e0\ub8b0\ub3c4',
    recurringSuggestionOccurrences: '\ud69f\uc218',
    recurringSuggestionRegister: '\uace0\uc815 \uc9c0\ucd9c\ub85c \ub4f1\ub85d',
    recurringSuggestionDismiss: '\ubb34\uc2dc',
    recurringSuggestionRegistered: '\uc774\ubbf8 \ub4f1\ub85d\ub428',
    registerFixedExpenseForecast: '\uace0\uc815 \uc9c0\ucd9c\ub85c \ub4f1\ub85d (\uc608\uce21)',
    restoreAccount: '\ubcf5\uad6c',
    restoreAccountHint:
      '\uacc4\uc88c \ubaa9\ub85d\uc5d0 \ub2e4\uc2dc \ud45c\uc2dc\ud569\ub2c8\ub2e4. \uac70\ub798 \uae30\ub85d\uc740 \uadf8\ub300\ub85c \uc720\uc9c0\ub429\ub2c8\ub2e4.',
    importBankSummaryNew: '\uc2e0\uaddc',
    importBankSummaryDuplicate: '\uc911\ubcf5',
    importBankSummaryError: '\uc624\ub958',
    importBankAdapterHokoku: '\ubd81\uad6d\uc740\ud589',
    importBankAdapterSbi: 'SBI\u65b0\u751f\u9280\u884c',
    roleOwner: '\uc18c\uc720\uc790',
    roleEditor: '\ud3b8\uc9d1\uc790',
    roleViewer: '\uc870\ud68c\uc790',
    statusActive: '\ud65c\uc131',
    statusDisabled: '\ube44\ud65c\uc131',
    mainNavigation: '\uc8fc \uba54\ub274',
    navDashboard: '\ub300\uc2dc\ubcf4\ub4dc',
    navAccounts: '\uacc4\uc88c',
    navPlanned: '\uc608\uc815',
    navOutflow: '\uc9c0\ucd9c',
    navSettings: '\uc124\uc815',
    navDashboardShort: '\ud648',
    navAccountsShort: '\uacc4\uc88c',
    navPlannedShort: '\uc608\uc815',
    navOutflowShort: '\uc9c0\ucd9c',
    navSettingsShort: '\uc124\uc815',
    settingsTitle: '\uc124\uc815',
    logout: '\ub85c\uadf8\uc544\uc6c3',
    languageControl: '\uc5b8\uc5b4',
    countryControl: '\uad6d\uac00',
    period30d: '30\uc77c',
    period3m: '3\uac1c\uc6d4',
    period12m: '12\uac1c\uc6d4',
    labelCountry: '\uad6d\uac00',
    labelAccountType: '\uacc4\uc88c \uc720\ud615',
    bankAccountNumber: '\uc740\ud589 \uacc4\uc88c\ubc88\ud638',
    bankAccountNumberHint:
      'CSV \uba85\uc138\uc758 \uacc4\uc88c\ubc88\ud638\uc640 \ub9de\ucd94\uba74 \uc784\ud3ec\ud2b8 \uacbd\uace0\uac00 \uc904\uc5b4\ub4ed\ub2c8\ub2e4 (\uc608: \ubd81\uad6d 0019609).',
    saveBankAccountNumber: '\uacc4\uc88c\ubc88\ud638 \uc800\uc7a5',
    importBankNoNewRows:
      '\uc2e0\uaddc \uac70\ub798\uac00 0\uac74\uc785\ub2c8\ub2e4. \uc794\uc561 \ub9de\ucdb4\ub9cc \ucf1c\uba74 \uba85\uc138 \ub0b4\uc5ed\uc774 \uc0dd\uae30\uc9c0 \uc54a\uc2b5\ub2c8\ub2e4.',
    initialBalance: '\ucd08\uae30 \uc794\uc561',
    allowNegativeBalance: '\ub9c8\uc774\ub108\uc2a4 \ud5c8\uc6a9',
    statusPlanned: '\uc608\uc815',
    statusPaid: '\uc644\ub8cc',
    statusAdjusted: '\uc870\uc815',
    yearSuffix: '\ub144'
  },
  ja: {
    accounts: '\u53e3\u5ea7',
    active: '\u6709\u52b9',
    activeAccounts: '\u6709\u52b9\u53e3\u5ea7\u30b5\u30de\u30ea\u30fc',
    amount: '\u91d1\u984d',
    availableBalance: '\u5229\u7528\u53ef\u80fd\u6b8b\u9ad8',
    balance: '\u6b8b\u9ad8',
    checking: '\u30bb\u30c3\u30b7\u30e7\u30f3\u78ba\u8a8d\u4e2d',
    selectPlaceholder: '\u9078\u629e',
    saved: '\u4fdd\u5b58\u3057\u307e\u3057\u305f',
    catchUpDone:
      '\u671f\u9650\u3092\u904e\u304e\u305f\u4e88\u5b9a\u9805\u76ee\u3092\u5143\u5e33\u306b\u53cd\u6620\u3057\u307e\u3057\u305f',
    requestFailed: '\u30ea\u30af\u30a8\u30b9\u30c8\u306b\u5931\u6557\u3057\u307e\u3057\u305f',
    errorGeneric: '\u30ea\u30af\u30a8\u30b9\u30c8\u3092\u51e6\u7406\u3067\u304d\u307e\u305b\u3093\u3067\u3057\u305f',
    errorUnauthorized: '\u30ed\u30b0\u30a4\u30f3\u304c\u5fc5\u8981\u3067\u3059',
    errorForbidden: '\u6a29\u9650\u304c\u3042\u308a\u307e\u305b\u3093',
    errorNetwork: '\u30cd\u30c3\u30c8\u30ef\u30fc\u30af\u30a8\u30e9\u30fc\u304c\u767a\u751f\u3057\u307e\u3057\u305f',
    errorConflict: '\u3059\u3067\u306b\u5b58\u5728\u3059\u308b\u304b\u3001\u64cd\u4f5c\u304c\u7981\u6b62\u3055\u308c\u307e\u3057\u305f',
    errorNotFound: '\u5bfe\u8c61\u304c\u898b\u3064\u304b\u308a\u307e\u305b\u3093',
    errorInvalidJson: '\u30ea\u30af\u30a8\u30b9\u30c8\u5f62\u5f0f\u304c\u6b63\u3057\u304f\u3042\u308a\u307e\u305b\u3093',
    accountTypeBank: '\u9280\u884c',
    accountTypeCash: '\u73fe\u91d1',
    accountTypeSavings: '\u8caf\u91d1',
    accountTypePrepaid: '\u30d7\u30ea\u30da\u30a4\u30c9',
    accountTypeOther: '\u305d\u306e\u4ed6',
    ownershipFamily: '\u5bb6\u65cf',
    ownershipSelf: '\u672c\u4eba',
    ownershipSpouse: '\u914d\u5076\u8005',
    ownershipChild: '\u5b50\u3069\u3082',
    ownershipShared: '\u5171\u6709',
    txnDeposit: '\u5165\u91d1',
    txnWithdrawal: '\u51fa\u91d1',
    txnTransfer: '\u632f\u66ff',
    countryJp: '\u65e5\u672c',
    countryKr: '\u97d3\u56fd',
    belowSafetyThreshold: '\u5b89\u5168\u57fa\u6e96\u672a\u6e80',
    negativeBalanceWarning: '\u30de\u30a4\u30ca\u30b9\u6b8b\u9ad8',
    createAccount: '\u53e3\u5ea7\u3092\u4f5c\u6210',
    currentBalance: '\u73fe\u5728\u6b8b\u9ad8',
    dashboard: '\u30c0\u30c3\u30b7\u30e5\u30dc\u30fc\u30c9',
    date: '\u65e5\u4ed8',
    dashboardOn:
      '\u30c0\u30c3\u30b7\u30e5\u30dc\u30fc\u30c9\u306b\u542b\u3081\u308b',
    dashboardOff:
      '\u30c0\u30c3\u30b7\u30e5\u30dc\u30fc\u30c9\u304b\u3089\u9664\u5916',
    eventCount: '\u4ef6',
    forecast: '\u4e88\u6e2c',
    forecastEmpty:
      '\u4e88\u6e2c\u9805\u76ee\u306f\u307e\u3060\u3042\u308a\u307e\u305b\u3093\u3002',
    inflow: '\u5165\u91d1',
    loadingDashboard:
      '\u30c0\u30c3\u30b7\u30e5\u30dc\u30fc\u30c9\u3092\u8aad\u307f\u8fbc\u307f\u4e2d',
    lowestExpectedBalance: '\u6700\u4f4e\u4e88\u60f3\u6b8b\u9ad8',
    emptyAccounts:
      '\u53e3\u5ea7\u306f\u307e\u3060\u3042\u308a\u307e\u305b\u3093\u3002',
    familyFinance:
      '\u5bb6\u65cf\u306e\u5bb6\u8a08\u30c0\u30c3\u30b7\u30e5\u30dc\u30fc\u30c9',
    googleLogin: 'Google\u3067\u30ed\u30b0\u30a4\u30f3',
    history: '\u53d6\u5f15\u5c65\u6b74',
    inactive: '\u7121\u52b9',
    loginPrompt:
      '\u5bb6\u65cf\u30ef\u30fc\u30af\u30b9\u30da\u30fc\u30b9\u306b\u30ed\u30b0\u30a4\u30f3\u3057\u3066\u3001\u53e3\u5ea7\u3068\u624b\u5165\u529b\u53d6\u5f15\u3092\u7ba1\u7406\u3057\u307e\u3059\u3002',
    name: '\u540d\u524d',
    newAccount: '\u65b0\u3057\u3044\u53e3\u5ea7',
    noTransactions:
      '\u9078\u629e\u3057\u305f\u53e3\u5ea7\u306e\u53d6\u5f15\u306f\u3042\u308a\u307e\u305b\u3093\u3002',
    note: '\u30e1\u30e2',
    outflow: '\u51fa\u91d1',
    period: '\u671f\u9593',
    periodInflow: '\u671f\u9593\u5165\u91d1',
    periodOutflow: '\u671f\u9593\u51fa\u91d1',
    plannedCashflow:
      '\u4e88\u5b9a\u30ad\u30e3\u30c3\u30b7\u30e5\u30d5\u30ed\u30fc',
    plannedCashflowEmpty:
      '\u4e88\u5b9a\u30ad\u30e3\u30c3\u30b7\u30e5\u30d5\u30ed\u30fc\u306f\u307e\u3060\u3042\u308a\u307e\u305b\u3093\u3002',
    plannedIncome: '\u4e88\u5b9a\u53ce\u5165',
    fixedExpense: '\u56fa\u5b9a\u652f\u51fa',
    ledgerSource: '\u5b9f\u7e3e\u306e\u53cd\u6620',
    ledgerSourceBank: '\u9280\u884cCSV\u30fb\u624b\u5165\u529b',
    ledgerSourceBankHint:
      '\u5b9f\u969b\u306e\u51fa\u91d1\u306f\u9280\u884cCSV\u3067\u53cd\u6620\u3055\u308c\u307e\u3059\u3002\u3053\u306e\u9805\u76ee\u306f\u4eca\u5f8c\u306e\u4e88\u60f3\u652f\u51fa\u306b\u306e\u307f\u4f7f\u3044\u307e\u3059\u3002',
    ledgerSourceSchedule: '\u4e88\u5b9a\u65e5\u306e\u81ea\u52d5\u4ed5\u8a33',
    ledgerSourceScheduleHint:
      'CSV\u306a\u3057\u3067\u4e88\u5b9a\u65e5\u306b\u81ea\u52d5\u3067\u53d6\u5f15\u3092\u4f5c\u6210\u3057\u307e\u3059\u3002',
    scheduledTransfer: '\u4e88\u5b9a\u632f\u66ff',
    scheduledOutflow: '\u4e88\u5b9a\u652f\u51fa\u7ba1\u7406',
    scheduledOutflowEmpty:
      '\u4e88\u5b9a\u652f\u51fa\u306f\u307e\u3060\u3042\u308a\u307e\u305b\u3093\u3002',
    cards: '\u30ab\u30fc\u30c9',
    loans: '\u30ed\u30fc\u30f3',
    card: '\u30ab\u30fc\u30c9',
    markBillingPaid: '\u6c7a\u6e08\u5b8c\u4e86',
    markLoanPaid: '\u8fd4\u6e08\u5b8c\u4e86',
    paymentDay: '\u652f\u6255\u65e5',
    paymentMonth: '\u8acb\u6c42\u6708',
    paymentDueDate: '\u652f\u6255\u4e88\u5b9a\u65e5',
    statementMonth: '\u660e\u7d30\u6708',
    principalAmount: '\u5143\u91d1',
    annualInterestRate: '\u5e74\u5229',
    termYears: '\u8fd4\u6e08\u671f\u9593(\u5e74)',
    recurringRule: '\u7e70\u308a\u8fd4\u3057\u30eb\u30fc\u30eb',
    oneTime: '\u4e00\u56de\u9650\u308a',
    monthlyDay: '\u6bce\u6708\u6307\u5b9a\u65e5',
    dayOfMonth: '\u65e5',
    startDate: '\u958b\u59cb\u65e5',
    expectedDate: '\u4e88\u5b9a\u65e5',
    fromAccount: '\u632f\u66ff\u5143',
    ownership: '\u6240\u6709',
    save: '\u4fdd\u5b58',
    safetyThreshold: '\u5b89\u5168\u57fa\u6e96',
    selectAccount: '\u53e3\u5ea7\u3092\u9078\u629e',
    transactions: '\u53d6\u5f15',
    transactionType: '\u53d6\u5f15\u7a2e\u5225',
    transferTarget: '\u632f\u66ff\u5148',
    workspace: '\u30ef\u30fc\u30af\u30b9\u30da\u30fc\u30b9',
    members: '\u30e1\u30f3\u30d0\u30fc\u7ba1\u7406',
    memberEmail: '\u30e1\u30fc\u30eb',
    memberRole: '\u6a29\u9650',
    memberStatus: '\u72b6\u614b',
    addMember: '\u30e1\u30f3\u30d0\u30fc\u8ffd\u52a0',
    disableMember: '\u7121\u52b9\u5316',
    disableFixedExpense: '\u56fa\u5b9a\u652f\u51fa\u3092\u7121\u52b9\u5316',
    disableFixedExpenseHint:
      '\u4e88\u5b9a\u4e00\u89a7\u304b\u3089\u975e\u8868\u793a\u306b\u3057\u307e\u3059\u3002\u53cd\u6620\u6e08\u307f\u306e\u53d6\u5f15\u306f\u6b8b\u3057\u307e\u3059\u3002',
    membersEmpty: '\u767b\u9332\u3055\u308c\u305f\u30e1\u30f3\u30d0\u30fc\u304c\u3042\u308a\u307e\u305b\u3093\u3002',
    exportWorkspace:
      '\u30ef\u30fc\u30af\u30b9\u30da\u30fc\u30b9JSON\u30a8\u30af\u30b9\u30dd\u30fc\u30c8',
    exportAccountCsv: '\u53d6\u5f15CSV',
    exportDownload: '\u30c0\u30a6\u30f3\u30ed\u30fc\u30c9',
    exportAccountSelect: '\u53e3\u5ea7\u9078\u629e',
    importBankCsv: '\u53d6\u5f15\u30fb\u53e3\u5ea7CSV\u30a4\u30f3\u30dd\u30fc\u30c8',
    importBankCsvSelect: 'CSV\u30d5\u30a1\u30a4\u30eb\u9078\u629e',
    importBankPreview: '\u30d7\u30ec\u30d3\u30e5\u30fc',
    importBankCommit: '\u30a4\u30f3\u30dd\u30fc\u30c8',
    importBankApplyInitial: '\u63d0\u6848\u306e\u521d\u671f\u6b8b\u9ad8\u3092\u9069\u7528',
    importBankReconcile: '\u660e\u7d30\u6700\u7d42\u6b8b\u9ad8\u306b\u5408\u308f\u305b\u8abf\u6574',
    importBankStatementEndBalance: '\u660e\u7d30\u6700\u7d42\u6b8b\u9ad8',
    importBankProjectedBalance: '\u53d6\u5f97\u5f8c\u306e\u4e88\u60f3\u6b8b\u9ad8',
    importBankReconcileDelta: '\u8abf\u6574\u984d',
    importBankReconcileDone:
      '\u3053\u306e\u53e3\u5ea7\u306f\u65e2\u306b\u6b8b\u9ad8\u3092\u5408\u308f\u305b\u6e08\u307f\u3067\u3059\u3002\u65b0\u898f\u53d6\u5f15\u306e\u307f\u8ffd\u52a0\u3055\u308c\u307e\u3059\u3002',
    archiveAccount: '\u53e3\u5ea7\u3092\u4fdd\u7ba1',
    archiveAccountHint:
      '\u4e00\u89a7\u304b\u3089\u975e\u8868\u793a\u306b\u3057\u3001\u53d6\u5f15\u5c65\u6b74\u306f\u6b8b\u3057\u307e\u3059\u3002',
    archivedAccounts: '\u4fdd\u7ba1\u3057\u305f\u53e3\u5ea7',
    archivedAccountsEmpty: '\u4fdd\u7ba1\u3057\u305f\u53e3\u5ea7\u306f\u3042\u308a\u307e\u305b\u3093\u3002',
    showArchivedAccounts: '\u4fdd\u7ba1\u4e00\u89a7\u3092\u8868\u793a',
    hideArchivedAccounts: '\u4fdd\u7ba1\u4e00\u89a7\u3092\u9589\u3058\u308b',
    recurringSuggestions: '\u7e70\u308a\u8fd4\u3057\u652f\u51fa\u306e\u63d0\u6848',
    recurringSuggestionsEmpty:
      '\u3053\u306e\u53e3\u5ea7\u3067\u306f\u307e\u3060\u7e70\u308a\u8fd4\u3057\u30d1\u30bf\u30fc\u30f3\u304c\u898b\u3064\u304b\u308a\u307e\u305b\u3093\u3002',
    showRecurringSuggestions: '\u63d0\u6848\u3092\u8868\u793a',
    hideRecurringSuggestions: '\u63d0\u6848\u3092\u975e\u8868\u793a',
    recurringSuggestionConfidence: '\u4fe1\u983c\u5ea6',
    recurringSuggestionOccurrences: '\u56de\u6570',
    recurringSuggestionRegister: '\u56fa\u5b9a\u652f\u51fa\u306b\u767b\u9332',
    recurringSuggestionDismiss: '\u975e\u8868\u793a',
    recurringSuggestionRegistered: '\u767b\u9332\u6e08\u307f',
    registerFixedExpenseForecast:
      '\u56fa\u5b9a\u652f\u51fa\u306b\u767b\u9332\uff08\u4e88\u6e2c\uff09',
    restoreAccount: '\u5fa9\u5143',
    restoreAccountHint:
      '\u53e3\u5ea7\u4e00\u89a7\u306b\u518d\u8868\u793a\u3057\u3001\u53d6\u5f15\u5c65\u6b74\u306f\u305d\u306e\u307e\u307e\u6b8b\u3057\u307e\u3059\u3002',
    importBankSummaryNew: '\u65b0\u898f',
    importBankSummaryDuplicate: '\u91cd\u8907',
    importBankSummaryError: '\u30a8\u30e9\u30fc',
    importBankAdapterHokoku: '\u5317\u56fd\u9280\u884c',
    importBankAdapterSbi: 'SBI\u65b0\u751f\u9280\u884c',
    roleOwner: '\u30aa\u30fc\u30ca\u30fc',
    roleEditor: '\u7de8\u96c6\u8005',
    roleViewer: '\u95b2\u89a7\u8005',
    statusActive: '\u6709\u52b9',
    statusDisabled: '\u7121\u52b9',
    mainNavigation: '\u30e1\u30a4\u30f3\u30e1\u30cb\u30e5\u30fc',
    navDashboard: '\u30c0\u30c3\u30b7\u30e5\u30dc\u30fc\u30c9',
    navAccounts: '\u53e3\u5ea7',
    navPlanned: '\u4e88\u5b9a',
    navOutflow: '\u652f\u51fa',
    navSettings: '\u8a2d\u5b9a',
    navDashboardShort: '\u30db\u30fc\u30e0',
    navAccountsShort: '\u53e3\u5ea7',
    navPlannedShort: '\u4e88\u5b9a',
    navOutflowShort: '\u652f\u51fa',
    navSettingsShort: '\u8a2d\u5b9a',
    settingsTitle: '\u8a2d\u5b9a',
    logout: '\u30ed\u30b0\u30a2\u30a6\u30c8',
    languageControl: '\u8a00\u8a9e',
    countryControl: '\u56fd',
    period30d: '30\u65e5',
    period3m: '3\u304b\u6708',
    period12m: '12\u304b\u6708',
    labelCountry: '\u56fd',
    labelAccountType: '\u53e3\u5ea7\u7a2e\u5225',
    bankAccountNumber: '\u9280\u884c\u53e3\u5ea7\u756a\u53f7',
    bankAccountNumberHint:
      'CSV\u660e\u7d30\u306e\u53e3\u5ea7\u756a\u53f7\u3068\u4e00\u81f4\u3059\u308b\u3068\u30a4\u30f3\u30dd\u30fc\u30c8\u6642\u306e\u8b66\u544a\u304c\u6e1b\u308a\u307e\u3059\uff08\u4f8b\uff1a\u5317\u56fd 0019609\uff09\u3002',
    saveBankAccountNumber: '\u53e3\u5ea7\u756a\u53f7\u3092\u4fdd\u5b58',
    importBankNoNewRows:
      '\u65b0\u898f\u53d6\u5f15\u304c0\u4ef6\u3067\u3059\u3002\u6b8b\u9ad8\u5408\u308f\u305b\u306e\u307f\u3067\u306f\u660e\u7d30\u884c\u304c\u4f5c\u3089\u308c\u307e\u305b\u3093\u3002',
    initialBalance: '\u521d\u671f\u6b8b\u9ad8',
    allowNegativeBalance: '\u30de\u30a4\u30ca\u30b9\u8a31\u53ef',
    statusPlanned: '\u4e88\u5b9a',
    statusPaid: '\u5b8c\u4e86',
    statusAdjusted: '\u8abf\u6574',
    yearSuffix: '\u5e74'
  }
} satisfies Record<Language, Record<string, string>>;

const accountTypes: AccountType[] = [
  'bank',
  'cash',
  'savings',
  'prepaid',
  'other'
];
const ownershipTypes: OwnershipType[] = [
  'family',
  'self',
  'spouse',
  'child',
  'shared'
];
const countries: Country[] = ['JP', 'KR'];
const dashboardPeriods: DashboardPeriod[] = ['30d', '3m', '12m'];
const transactionTypes: TransactionType[] = [
  'manual_deposit',
  'manual_withdrawal',
  'manual_transfer'
];

function accountTypeLabel(language: Language, type: AccountType): string {
  const t = copy[language];
  switch (type) {
    case 'bank':
      return t.accountTypeBank;
    case 'cash':
      return t.accountTypeCash;
    case 'savings':
      return t.accountTypeSavings;
    case 'prepaid':
      return t.accountTypePrepaid;
    default:
      return t.accountTypeOther;
  }
}

function ownershipLabel(language: Language, type: OwnershipType): string {
  const t = copy[language];
  switch (type) {
    case 'family':
      return t.ownershipFamily;
    case 'self':
      return t.ownershipSelf;
    case 'spouse':
      return t.ownershipSpouse;
    case 'child':
      return t.ownershipChild;
    default:
      return t.ownershipShared;
  }
}

function transactionLabel(language: Language, type: TransactionType): string {
  const t = copy[language];
  switch (type) {
    case 'manual_deposit':
      return t.txnDeposit;
    case 'manual_withdrawal':
      return t.txnWithdrawal;
    default:
      return t.txnTransfer;
  }
}

function countryLabel(language: Language, country: Country): string {
  const t = copy[language];
  return country === 'JP' ? t.countryJp : t.countryKr;
}

function periodLabel(language: Language, period: DashboardPeriod): string {
  const t = copy[language];
  if (period === '30d') return t.period30d;
  if (period === '3m') return t.period3m;
  return t.period12m;
}

function scheduleStatusLabel(language: Language, status: string): string {
  const t = copy[language];
  if (status === 'paid') return t.statusPaid;
  if (status === 'adjusted') return t.statusAdjusted;
  return t.statusPlanned;
}

const recurringFrequencies: RecurringFrequency[] = ['once', 'monthly_day'];

function browserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'ko';
  return navigator.language.toLowerCase().startsWith('ja') ? 'ja' : 'ko';
}

function formatLocalDate(date = new Date()): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function emptyAccountForm(country: Country): AccountFormState {
  return {
    name: '',
    country,
    accountType: 'bank',
    ownershipType: 'family',
    initialBalance: '0',
    includeInDashboard: true,
    allowNegativeBalance: true,
    safetyThreshold: '',
    bankAccountNumber: ''
  };
}

function accountUsesBankCsvLink(
  country: Country,
  accountType: AccountType
): boolean {
  return country === 'JP' && (accountType === 'bank' || accountType === 'savings');
}

function emptyTransactionForm(): TransactionFormState {
  return {
    type: 'manual_deposit',
    accountId: '',
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    localDate: formatLocalDate(),
    note: ''
  };
}

function emptyRecurringForm(): RecurringFormState {
  const today = formatLocalDate();
  return {
    frequency: 'once',
    startDate: today,
    dayOfMonth: `${new Date(`${today}T00:00:00`).getDate()}`
  };
}

function emptyIncomeOccurrenceForm(): IncomeOccurrenceFormState {
  return {
    accountId: '',
    amount: '',
    expectedDate: formatLocalDate(),
    note: ''
  };
}

function emptyFixedExpenseForm(): FixedExpenseFormState {
  return {
    name: '',
    accountId: '',
    amount: '',
    ledgerSource: 'bank',
    recurring: emptyRecurringForm()
  };
}

function emptyScheduledTransferForm(): ScheduledTransferFormState {
  return {
    name: '',
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    recurring: emptyRecurringForm()
  };
}

function currentPaymentMonth(): string {
  return formatLocalDate().slice(0, 7);
}

function emptyCardForm(): CardFormState {
  return {
    name: '',
    ownershipType: 'family',
    paymentAccountId: '',
    paymentDay: '1'
  };
}

function emptyBillingCycleForm(): BillingCycleFormState {
  const today = formatLocalDate();
  return {
    cardId: '',
    paymentMonth: currentPaymentMonth(),
    paymentDueDate: today,
    amount: '',
    statementMonth: ''
  };
}

function emptyLoanForm(): LoanFormState {
  return {
    name: '',
    principalAmount: '',
    annualInterestRate: '0.03',
    startDate: formatLocalDate(),
    termYears: '30',
    paymentDay: '1',
    paymentAccountId: ''
  };
}

function emptyMemberForm(): MemberFormState {
  return { email: '', role: 'editor' };
}

function memberRoleLabel(
  role: string,
  t: (typeof copy)[Language]
): string {
  if (role === 'owner') return t.roleOwner;
  if (role === 'viewer') return t.roleViewer;
  return t.roleEditor;
}

function memberStatusLabel(
  status: WorkspaceMemberRecord['status'],
  t: (typeof copy)[Language]
): string {
  return status === 'disabled' ? t.statusDisabled : t.statusActive;
}

export function App({
  initialAuthState = 'checking',
  initialSession = null,
  initialTab = 'dashboard'
}: AppProps = {}) {
  const [language, setLanguage] = useState<Language>(browserLanguage);
  const [country, setCountry] = useState<Country>('JP');
  const [session, setSession] = useState<SessionInfo | null>(initialSession);
  const [authState, setAuthState] = useState<AuthState>(initialAuthState);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [showArchivedAccounts, setShowArchivedAccounts] = useState(false);
  const [showRecurringSuggestions, setShowRecurringSuggestions] = useState(false);
  const [recurringSuggestions, setRecurringSuggestions] = useState<
    RecurringExpenseSuggestion[]
  >([]);
  const [transactions, setTransactions] = useState<LedgerTransaction[]>([]);
  const [dashboardPeriod, setDashboardPeriod] =
    useState<DashboardPeriod>('30d');
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const dashboardRequestSequence = useRef(0);
  const plannedCashflowRequestSequence = useRef(0);
  const scheduledOutflowRequestSequence = useRef(0);
  const catchUpStarted = useRef(false);
  const [scheduledOutflowTab, setScheduledOutflowTab] =
    useState<ScheduledOutflowTab>('cards');
  const [activeTab, setActiveTab] = useState<AppTab>(initialTab);
  const [cards, setCards] = useState<CardRecord[]>([]);
  const [billingCycles, setBillingCycles] = useState<CardBillingCycleRecord[]>(
    []
  );
  const [loans, setLoans] = useState<LoanRecord[]>([]);
  const [loanPaymentSchedules, setLoanPaymentSchedules] = useState<
    LoanPaymentScheduleRecord[]
  >([]);
  const [cardForm, setCardForm] = useState<CardFormState>(emptyCardForm);
  const [billingCycleForm, setBillingCycleForm] =
    useState<BillingCycleFormState>(emptyBillingCycleForm);
  const [loanForm, setLoanForm] = useState<LoanFormState>(emptyLoanForm);
  const [accountForm, setAccountForm] = useState<AccountFormState>(() =>
    emptyAccountForm('JP')
  );
  const [transactionForm, setTransactionForm] =
    useState<TransactionFormState>(emptyTransactionForm);
  const [incomeOccurrenceForm, setIncomeOccurrenceForm] =
    useState<IncomeOccurrenceFormState>(emptyIncomeOccurrenceForm);
  const [fixedExpenseForm, setFixedExpenseForm] =
    useState<FixedExpenseFormState>(emptyFixedExpenseForm);
  const [scheduledTransferForm, setScheduledTransferForm] =
    useState<ScheduledTransferFormState>(emptyScheduledTransferForm);
  const [incomeOccurrences, setIncomeOccurrences] = useState<
    IncomeOccurrence[]
  >([]);
  const [fixedExpenses, setFixedExpenses] = useState<FixedExpense[]>([]);
  const [scheduledTransfers, setScheduledTransfers] = useState<
    ScheduledTransfer[]
  >([]);
  const [notice, setNotice] = useState('');
  const [noticeKind, setNoticeKind] = useState<'success' | 'error'>('success');
  const [busy, setBusy] = useState(false);
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMemberRecord[]>(
    []
  );
  const [memberForm, setMemberForm] = useState<MemberFormState>(emptyMemberForm);
  const [bankImportCsvText, setBankImportCsvText] = useState('');
  const [bankImportPreview, setBankImportPreview] =
    useState<BankImportPreviewResponse | null>(null);
  const [bankImportApplyInitial, setBankImportApplyInitial] = useState(false);
  const [bankImportApplyReconcile, setBankImportApplyReconcile] = useState(false);
  const [linkedBankAccountNumber, setLinkedBankAccountNumber] = useState('');

  const t = copy[language];
  const apiErrorMessages: ApiErrorMessages = useMemo(
    () => ({
      errorGeneric: t.errorGeneric,
      errorUnauthorized: t.errorUnauthorized,
      errorForbidden: t.errorForbidden,
      errorNetwork: t.errorNetwork,
      errorConflict: t.errorConflict,
      errorNotFound: t.errorNotFound,
      errorInvalidJson: t.errorInvalidJson
    }),
    [t]
  );
  const isOwner = session?.member.role === 'owner';
  const canEditLedger =
    session?.member.role === 'owner' || session?.member.role === 'editor';
  const appTabs = useMemo(() => {
    const tabs: Array<{ id: AppTab; label: string; shortLabel: string }> = [
      {
        id: 'dashboard',
        label: t.navDashboard,
        shortLabel: t.navDashboardShort
      },
      {
        id: 'accounts',
        label: t.navAccounts,
        shortLabel: t.navAccountsShort
      },
      {
        id: 'planned',
        label: t.navPlanned,
        shortLabel: t.navPlannedShort
      },
      {
        id: 'outflow',
        label: t.navOutflow,
        shortLabel: t.navOutflowShort
      }
    ];
    tabs.push({
      id: 'settings',
      label: t.navSettings,
      shortLabel: t.navSettingsShort
    });
    return tabs;
  }, [t]);

  function clearNotice() {
    setNotice('');
  }

  function reportSuccess(message = t.saved) {
    setNotice(message);
    setNoticeKind('success');
  }

  function reportError(message: string) {
    setNotice(message);
    setNoticeKind('error');
  }

  async function changeLanguage(locale: Language) {
    setLanguage(locale);
    try {
      const response = await fetch('/api/me/preferences', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferredLocale: locale })
      });
      if (!response.ok) {
        reportError(await readApiError(response, apiErrorMessages));
        return;
      }
      setSession((current) =>
        current
          ? {
              ...current,
              user: { ...current.user, preferredLocale: locale }
            }
          : null
      );
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.errorNetwork);
    }
  }

  async function logout() {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch {
      // clear local session even if logout request fails
    }
    setSession(null);
    setAuthState('signed-out');
    setActiveTab('dashboard');
  }

  const visibleAccounts = useMemo(
    () =>
      accounts.filter(
        (account) => account.country === country && !account.archivedAt
      ),
    [accounts, country]
  );
  const archivedAccounts = useMemo(
    () =>
      accounts
        .filter(
          (account) => account.country === country && account.archivedAt
        )
        .sort((left, right) =>
          (right.archivedAt ?? '').localeCompare(left.archivedAt ?? '')
        ),
    [accounts, country]
  );
  const selectedAccount =
    visibleAccounts.find((account) => account.id === selectedAccountId) ?? null;

  useEffect(() => {
    setLinkedBankAccountNumber(selectedAccount?.bankAccountNumber ?? '');
  }, [selectedAccount?.bankAccountNumber, selectedAccountId]);

  const dashboardCurrency: Currency = country === 'JP' ? 'JPY' : 'KRW';
  const displayedDashboard =
    dashboard?.currency === dashboardCurrency &&
    dashboard.period === dashboardPeriod
      ? dashboard
      : null;
  const visibleAccountIds = useMemo(
    () => new Set(visibleAccounts.map((account) => account.id)),
    [visibleAccounts]
  );
  const visibleIncomeOccurrences = incomeOccurrences.filter((occurrence) =>
    visibleAccountIds.has(occurrence.accountId)
  );
  const visibleFixedExpenses = fixedExpenses.filter((expense) =>
    visibleAccountIds.has(expense.accountId)
  );
  const visibleScheduledTransfers = scheduledTransfers.filter(
    (transfer) =>
      visibleAccountIds.has(transfer.fromAccountId) ||
      visibleAccountIds.has(transfer.toAccountId)
  );
  const visibleCards = useMemo(
    () => cards.filter((card) => card.country === country),
    [cards, country]
  );
  const visibleCardIds = useMemo(
    () => new Set(visibleCards.map((card) => card.id)),
    [visibleCards]
  );
  const visibleBillingCycles = useMemo(
    () => billingCycles.filter((cycle) => visibleCardIds.has(cycle.cardId)),
    [billingCycles, visibleCardIds]
  );
  const visibleLoans = useMemo(
    () => loans.filter((loan) => loan.country === country),
    [loans, country]
  );
  const visibleLoanIds = useMemo(
    () => new Set(visibleLoans.map((loan) => loan.id)),
    [visibleLoans]
  );
  const visibleLoanSchedules = useMemo(
    () =>
      loanPaymentSchedules.filter((schedule) =>
        visibleLoanIds.has(schedule.loanId)
      ),
    [loanPaymentSchedules, visibleLoanIds]
  );
  const cardNameById = useMemo(
    () => new Map(visibleCards.map((card) => [card.id, card.name])),
    [visibleCards]
  );
  const loanNameById = useMemo(
    () => new Map(visibleLoans.map((loan) => [loan.id, loan.name])),
    [visibleLoans]
  );

  useEffect(() => {
    if (initialAuthState !== 'checking') return;
    let active = true;

    fetch('/api/me')
      .then(async (response) => {
        if (!response.ok) throw new Error('signed-out');
        return (await response.json()) as SessionInfo;
      })
      .then((me) => {
        if (!active) return;
        setSession(me);
        setLanguage(
          resolveLocale(
            me.user.preferredLocale,
            me.workspace.defaultLocale,
            browserLanguage()
          )
        );
        setAuthState('signed-in');
      })
      .catch(() => {
        if (!active) return;
        setAuthState('signed-out');
      });

    return () => {
      active = false;
    };
  }, [initialAuthState]);

  useEffect(() => {
    if (!session) return;
    setLanguage(
      resolveLocale(
        session.user.preferredLocale,
        session.workspace.defaultLocale,
        browserLanguage()
      )
    );
  }, [session?.user.preferredLocale, session?.workspace.defaultLocale]);

  useEffect(() => {
    if (authState !== 'signed-in') return;
    void loadAccounts();
  }, [authState]);

  useEffect(() => {
    if (authState !== 'signed-in') {
      catchUpStarted.current = false;
      return;
    }
    if (catchUpStarted.current) return;
    catchUpStarted.current = true;

    void (async () => {
      try {
        const response = await fetch('/api/processing/catch-up', {
          method: 'POST',
          credentials: 'include'
        });
        if (!response.ok) return;

        const body = (await response.json()) as {
          catchUp?: { processed: number };
        };
        if ((body.catchUp?.processed ?? 0) <= 0) return;

        reportSuccess(copy[language].catchUpDone);
        await Promise.all([
          loadAccounts(),
          loadDashboard(dashboardCurrency, dashboardPeriod),
          loadPlannedCashflow(),
          loadScheduledOutflow()
        ]);
      } catch {
        // silent: catch-up is best-effort on sign-in
      }
    })();
  }, [authState, dashboardCurrency, dashboardPeriod, language]);

  useEffect(() => {
    if (authState !== 'signed-in') return;
    void loadDashboard(dashboardCurrency, dashboardPeriod);
  }, [authState, dashboardCurrency, dashboardPeriod]);

  useEffect(() => {
    if (authState !== 'signed-in') return;
    void loadPlannedCashflow();
  }, [authState]);

  useEffect(() => {
    if (authState !== 'signed-in') return;
    void loadScheduledOutflow();
  }, [authState]);

  useEffect(() => {
    if (authState !== 'signed-in' || !isOwner) return;
    void loadWorkspaceMembers();
  }, [authState, isOwner]);

  useEffect(() => {
    setAccountForm((current) => ({ ...current, country }));
  }, [country]);

  useEffect(() => {
    setSelectedAccountId((current) => {
      if (
        current &&
        visibleAccounts.some((account) => account.id === current)
      ) {
        return current;
      }

      return visibleAccounts[0]?.id ?? '';
    });
  }, [visibleAccounts]);

  useEffect(() => {
    setTransactionForm((current) => ({
      ...current,
      accountId: visibleAccountIds.has(current.accountId)
        ? current.accountId
        : '',
      fromAccountId: visibleAccountIds.has(current.fromAccountId)
        ? current.fromAccountId
        : '',
      toAccountId: visibleAccountIds.has(current.toAccountId)
        ? current.toAccountId
        : ''
    }));
    setIncomeOccurrenceForm((current) => ({
      ...current,
      accountId: visibleAccountIds.has(current.accountId)
        ? current.accountId
        : ''
    }));
    setFixedExpenseForm((current) => ({
      ...current,
      accountId: visibleAccountIds.has(current.accountId)
        ? current.accountId
        : ''
    }));
    setScheduledTransferForm((current) => ({
      ...current,
      fromAccountId: visibleAccountIds.has(current.fromAccountId)
        ? current.fromAccountId
        : '',
      toAccountId: visibleAccountIds.has(current.toAccountId)
        ? current.toAccountId
        : ''
    }));
    setCardForm((current) => ({
      ...current,
      paymentAccountId: visibleAccountIds.has(current.paymentAccountId)
        ? current.paymentAccountId
        : ''
    }));
    setBillingCycleForm((current) => ({
      ...current,
      cardId: visibleCardIds.has(current.cardId) ? current.cardId : ''
    }));
    setLoanForm((current) => ({
      ...current,
      paymentAccountId: visibleAccountIds.has(current.paymentAccountId)
        ? current.paymentAccountId
        : ''
    }));
  }, [visibleAccountIds, visibleCardIds]);

  useEffect(() => {
    if (!selectedAccountId) {
      setTransactions([]);
      return;
    }

    let active = true;
    fetch(`/api/accounts/${selectedAccountId}/transactions`)
      .then(async (response) => {
        if (!response.ok) throw new Error('transactions_failed');
        return (await response.json()) as { transactions: LedgerTransaction[] };
      })
      .then((body) => {
        if (active) setTransactions(body.transactions);
      })
      .catch((error) => {
        if (active)
          reportError(error instanceof Error ? error.message : t.requestFailed);
      });

    return () => {
      active = false;
    };
  }, [selectedAccountId]);

  useEffect(() => {
    if (!selectedAccountId) {
      setRecurringSuggestions([]);
      return;
    }

    let active = true;
    fetch(`/api/accounts/${selectedAccountId}/recurring-expense-suggestions`)
      .then(async (response) => {
        if (!response.ok) throw new Error('recurring_suggestions_failed');
        return (await response.json()) as {
          suggestions: RecurringExpenseSuggestion[];
        };
      })
      .then((body) => {
        if (active) setRecurringSuggestions(body.suggestions);
      })
      .catch(() => {
        if (active) setRecurringSuggestions([]);
      });

    return () => {
      active = false;
    };
  }, [selectedAccountId]);

  async function loadAccounts() {
    const response = await fetch('/api/accounts');
    if (!response.ok) {
      reportError(await readApiError(response, apiErrorMessages));
      return;
    }

    const body = (await response.json()) as { accounts: Account[] };
    setAccounts(body.accounts);
  }

  async function loadDashboard(
    currency = dashboardCurrency,
    period = dashboardPeriod
  ) {
    const requestSequence = dashboardRequestSequence.current + 1;
    dashboardRequestSequence.current = requestSequence;
    setDashboardLoading(true);
    const params = new URLSearchParams({ currency, period });

    try {
      const response = await fetch(`/api/dashboard?${params.toString()}`);
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      const body = (await response.json()) as { dashboard: DashboardResponse };
      if (
        isLatestDashboardRequest(
          requestSequence,
          dashboardRequestSequence.current
        )
      ) {
        setDashboard(body.dashboard);
      }
    } catch (error) {
      if (
        isLatestDashboardRequest(
          requestSequence,
          dashboardRequestSequence.current
        )
      ) {
        reportError(error instanceof Error ? error.message : t.requestFailed);
      }
    } finally {
      if (
        isLatestDashboardRequest(
          requestSequence,
          dashboardRequestSequence.current
        )
      ) {
        setDashboardLoading(false);
      }
    }
  }

  async function loadPlannedCashflow() {
    const requestSequence = plannedCashflowRequestSequence.current + 1;
    plannedCashflowRequestSequence.current = requestSequence;

    try {
      const [incomeResponse, expensesResponse, transfersResponse] =
        await Promise.all([
          fetch('/api/income-occurrences'),
          fetch('/api/fixed-expenses'),
          fetch('/api/scheduled-transfers')
        ]);

      if (!incomeResponse.ok) throw new Error(await readApiError(incomeResponse, apiErrorMessages));
      if (!expensesResponse.ok) {
        throw new Error(await readApiError(expensesResponse, apiErrorMessages));
      }
      if (!transfersResponse.ok) {
        throw new Error(await readApiError(transfersResponse, apiErrorMessages));
      }

      const incomeBody = (await incomeResponse.json()) as {
        incomeOccurrences: IncomeOccurrence[];
      };
      const expensesBody = (await expensesResponse.json()) as {
        fixedExpenses: FixedExpense[];
      };
      const transfersBody = (await transfersResponse.json()) as {
        scheduledTransfers: ScheduledTransfer[];
      };
      if (
        isLatestDashboardRequest(
          requestSequence,
          plannedCashflowRequestSequence.current
        )
      ) {
        setIncomeOccurrences(incomeBody.incomeOccurrences);
        setFixedExpenses(expensesBody.fixedExpenses);
        setScheduledTransfers(transfersBody.scheduledTransfers);
      }
    } catch (error) {
      if (
        isLatestDashboardRequest(
          requestSequence,
          plannedCashflowRequestSequence.current
        )
      ) {
        reportError(error instanceof Error ? error.message : t.requestFailed);
      }
    }
  }

  async function loadScheduledOutflow() {
    const requestSequence = scheduledOutflowRequestSequence.current + 1;
    scheduledOutflowRequestSequence.current = requestSequence;

    try {
      const [cardsResponse, billingCyclesResponse, loansResponse, schedulesResponse] =
        await Promise.all([
          fetch('/api/cards'),
          fetch('/api/card-billing-cycles'),
          fetch('/api/loans'),
          fetch('/api/loan-payment-schedules')
        ]);

      if (!cardsResponse.ok) throw new Error(await readApiError(cardsResponse, apiErrorMessages));
      if (!billingCyclesResponse.ok) {
        throw new Error(await readApiError(billingCyclesResponse, apiErrorMessages));
      }
      if (!loansResponse.ok) throw new Error(await readApiError(loansResponse, apiErrorMessages));
      if (!schedulesResponse.ok) {
        throw new Error(await readApiError(schedulesResponse, apiErrorMessages));
      }

      const cardsBody = (await cardsResponse.json()) as { cards: CardRecord[] };
      const billingCyclesBody = (await billingCyclesResponse.json()) as {
        billingCycles: CardBillingCycleRecord[];
      };
      const loansBody = (await loansResponse.json()) as { loans: LoanRecord[] };
      const schedulesBody = (await schedulesResponse.json()) as {
        loanPaymentSchedules: LoanPaymentScheduleRecord[];
      };

      if (
        isLatestDashboardRequest(
          requestSequence,
          scheduledOutflowRequestSequence.current
        )
      ) {
        setCards(cardsBody.cards);
        setBillingCycles(billingCyclesBody.billingCycles);
        setLoans(loansBody.loans);
        setLoanPaymentSchedules(schedulesBody.loanPaymentSchedules);
      }
    } catch (error) {
      if (
        isLatestDashboardRequest(
          requestSequence,
          scheduledOutflowRequestSequence.current
        )
      ) {
        reportError(error instanceof Error ? error.message : t.requestFailed);
      }
    }
  }

  async function loadWorkspaceMembers() {
    try {
      const response = await fetch('/api/workspace-members');
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      const body = (await response.json()) as { members: WorkspaceMemberRecord[] };
      setWorkspaceMembers(body.members);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    }
  }

  async function createWorkspaceMemberRecord(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    clearNotice();

    try {
      const response = await fetch('/api/workspace-members', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(memberForm)
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      setMemberForm(emptyMemberForm());
      reportSuccess();
      await loadWorkspaceMembers();
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function disableWorkspaceMember(memberId: string) {
    setBusy(true);
    clearNotice();

    try {
      const response = await fetch(`/api/workspace-members/${memberId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status: 'disabled' })
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      reportSuccess();
      await loadWorkspaceMembers();
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function createCardRecord(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    clearNotice();

    const paymentAccountId = visibleAccountIds.has(cardForm.paymentAccountId)
      ? cardForm.paymentAccountId
      : selectedAccountId;
    const payload = {
      name: cardForm.name,
      ownershipType: cardForm.ownershipType,
      paymentAccountId,
      paymentDay: toInteger(cardForm.paymentDay)
    };

    try {
      const response = await fetch('/api/cards', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      setCardForm((current) => ({
        ...emptyCardForm(),
        paymentAccountId: current.paymentAccountId
      }));
      reportSuccess();
      await loadScheduledOutflow();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function createBillingCycle(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    clearNotice();

    const payload = {
      cardId: billingCycleForm.cardId,
      paymentMonth: billingCycleForm.paymentMonth,
      paymentDueDate: billingCycleForm.paymentDueDate,
      amount: toInteger(billingCycleForm.amount),
      statementMonth:
        billingCycleForm.statementMonth.trim().length > 0
          ? billingCycleForm.statementMonth
          : undefined
    };

    try {
      const response = await fetch('/api/card-billing-cycles', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      setBillingCycleForm((current) => ({
        ...emptyBillingCycleForm(),
        cardId: current.cardId
      }));
      reportSuccess();
      await loadScheduledOutflow();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function createLoanRecord(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    clearNotice();

    const paymentAccountId = visibleAccountIds.has(loanForm.paymentAccountId)
      ? loanForm.paymentAccountId
      : selectedAccountId;
    const payload = {
      name: loanForm.name,
      principalAmount: toInteger(loanForm.principalAmount),
      annualInterestRate: toNumber(loanForm.annualInterestRate),
      startDate: loanForm.startDate,
      termYears: toInteger(loanForm.termYears),
      paymentDay: toInteger(loanForm.paymentDay),
      paymentAccountId
    };

    try {
      const response = await fetch('/api/loans', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      setLoanForm((current) => ({
        ...emptyLoanForm(),
        paymentAccountId: current.paymentAccountId
      }));
      reportSuccess();
      await loadScheduledOutflow();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function markBillingCyclePaid(billingCycleId: string) {
    setBusy(true);
    clearNotice();

    try {
      const response = await fetch(
        `/api/card-billing-cycles/${billingCycleId}`,
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ status: 'paid' })
        }
      );
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      reportSuccess();
      await loadScheduledOutflow();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function markLoanSchedulePaid(scheduleId: string) {
    setBusy(true);
    clearNotice();

    try {
      const response = await fetch(`/api/loan-payment-schedules/${scheduleId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status: 'paid' })
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      reportSuccess();
      await loadScheduledOutflow();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  function canMarkBillingCyclePaid(status: CardBillingCycleStatus) {
    return status === 'planned' || status === 'adjusted';
  }

  function canMarkLoanSchedulePaid(status: LoanPaymentScheduleStatus) {
    return status === 'planned' || status === 'adjusted';
  }

  async function createIncomeOccurrence(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    clearNotice();

    const accountId = visibleAccountIds.has(incomeOccurrenceForm.accountId)
      ? incomeOccurrenceForm.accountId
      : selectedAccountId;
    const payload = {
      accountId,
      amount: toInteger(incomeOccurrenceForm.amount),
      expectedDate: incomeOccurrenceForm.expectedDate,
      note: optionalNote(incomeOccurrenceForm.note)
    };

    try {
      const response = await fetch('/api/income-occurrences', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      const body = (await response.json()) as {
        incomeOccurrence: IncomeOccurrence;
      };
      setIncomeOccurrences((current) => [...current, body.incomeOccurrence]);
      setIncomeOccurrenceForm((current) => ({
        ...emptyIncomeOccurrenceForm(),
        accountId: current.accountId
      }));
      reportSuccess();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function createFixedExpense(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    clearNotice();

    const accountId = visibleAccountIds.has(fixedExpenseForm.accountId)
      ? fixedExpenseForm.accountId
      : selectedAccountId;
    const payload = {
      name: fixedExpenseForm.name,
      accountId,
      amount: toInteger(fixedExpenseForm.amount),
      ledgerSource: fixedExpenseForm.ledgerSource,
      recurringRule: recurringRulePayload(fixedExpenseForm.recurring)
    };

    try {
      const response = await fetch('/api/fixed-expenses', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      const body = (await response.json()) as { fixedExpense: FixedExpense };
      setFixedExpenses((current) => [...current, body.fixedExpense]);
      setFixedExpenseForm((current) => ({
        ...emptyFixedExpenseForm(),
        accountId: current.accountId
      }));
      reportSuccess();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function disableFixedExpense(expense: FixedExpense) {
    const confirmed = window.confirm(
      `${t.disableFixedExpense}: ${expense.name}\n\n${t.disableFixedExpenseHint}`
    );
    if (!confirmed) return;

    clearNotice();
    setBusy(true);
    try {
      const response = await fetch(`/api/fixed-expenses/${expense.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ isActive: false })
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, apiErrorMessages));
      }

      const body = (await response.json()) as { fixedExpense: FixedExpense };
      setFixedExpenses((current) =>
        current.map((candidate) =>
          candidate.id === body.fixedExpense.id ? body.fixedExpense : candidate
        )
      );
      reportSuccess();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function registerRecurringSuggestion(
    suggestion: RecurringExpenseSuggestion
  ) {
    if (!selectedAccountId || suggestion.alreadyRegistered) return;

    setBusy(true);
    clearNotice();

    try {
      const ledgerSource =
        selectedAccount?.country === 'KR' ? 'schedule' : 'bank';
      const response = await fetch('/api/fixed-expenses', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: suggestion.label,
          accountId: selectedAccountId,
          amount: suggestion.amount,
          ledgerSource,
          includeInForecast: true,
          recurringRule: suggestion.suggestedRecurringRule
        })
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      const body = (await response.json()) as { fixedExpense: FixedExpense };
      setFixedExpenses((current) => [...current, body.fixedExpense]);
      setRecurringSuggestions((current) =>
        current.map((candidate) =>
          candidate.id === suggestion.id
            ? { ...candidate, alreadyRegistered: true }
            : candidate
        )
      );
      reportSuccess();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function dismissRecurringSuggestionItem(suggestionId: string) {
    setBusy(true);
    clearNotice();

    try {
      const response = await fetch('/api/recurring-expense-suggestions/dismiss', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ suggestionId })
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      setRecurringSuggestions((current) =>
        current.filter((candidate) => candidate.id !== suggestionId)
      );
      reportSuccess();
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  function openFixedExpenseFromTransaction(transaction: LedgerTransaction) {
    if (!selectedAccountId) return;

    setFixedExpenseForm({
      name: transaction.note?.trim() || transactionLabel(language, transaction.type as TransactionType),
      accountId: selectedAccountId,
      amount: String(Math.abs(transaction.entry.amount)),
      ledgerSource: 'bank',
      recurring: {
        frequency: 'monthly_day',
        startDate: formatLocalDate(),
        dayOfMonth: String(
          Number.parseInt(transaction.localDate.slice(8, 10), 10) || 1
        )
      }
    });
    setActiveTab('planned');
  }

  async function createScheduledTransfer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    clearNotice();

    const payload = {
      name: scheduledTransferForm.name,
      fromAccountId: scheduledTransferForm.fromAccountId,
      toAccountId: scheduledTransferForm.toAccountId,
      amount: toInteger(scheduledTransferForm.amount),
      recurringRule: recurringRulePayload(scheduledTransferForm.recurring)
    };

    try {
      const response = await fetch('/api/scheduled-transfers', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      const body = (await response.json()) as {
        scheduledTransfer: ScheduledTransfer;
      };
      setScheduledTransfers((current) => [
        ...current,
        body.scheduledTransfer
      ]);
      setScheduledTransferForm((current) => ({
        ...emptyScheduledTransferForm(),
        fromAccountId: current.fromAccountId,
        toAccountId: current.toAccountId
      }));
      reportSuccess();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function createAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    clearNotice();

    const payload: Record<string, unknown> = {
      name: accountForm.name,
      country: accountForm.country,
      accountType: accountForm.accountType,
      ownershipType: accountForm.ownershipType,
      initialBalance: toInteger(accountForm.initialBalance),
      includeInDashboard: accountForm.includeInDashboard,
      allowNegativeBalance: accountForm.allowNegativeBalance,
      safetyThreshold:
        accountForm.safetyThreshold.trim().length > 0
          ? toInteger(accountForm.safetyThreshold)
          : null
    };
    if (accountUsesBankCsvLink(accountForm.country, accountForm.accountType)) {
      const bankAccountNumber = accountForm.bankAccountNumber.trim();
      if (bankAccountNumber.length > 0) {
        payload.bankAccountNumber = bankAccountNumber;
      }
    }

    try {
      const response = await fetch('/api/accounts', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      const body = (await response.json()) as { account: Account };
      setAccounts((current) => [...current, body.account]);
      setCountry(body.account.country);
      setSelectedAccountId(body.account.id);
      setAccountForm(emptyAccountForm(body.account.country));
      reportSuccess();
      await loadDashboard(body.account.currency, dashboardPeriod);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function patchAccount(account: Account, updates: Partial<Account>) {
    clearNotice();
    const response = await fetch(`/api/accounts/${account.id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      reportError(await readApiError(response, apiErrorMessages));
      return;
    }

    const body = (await response.json()) as { account: Account };
    setAccounts((current) =>
      current.map((candidate) =>
        candidate.id === body.account.id ? body.account : candidate
      )
    );
    await loadDashboard(body.account.currency, dashboardPeriod);
  }

  async function archiveAccount(account: Account) {
    const confirmed = window.confirm(
      `${t.archiveAccount}: ${account.name}\n\n${t.archiveAccountHint}`
    );
    if (!confirmed) return;

    clearNotice();
    setBusy(true);
    try {
      const response = await fetch(`/api/accounts/${account.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ archived: true })
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, apiErrorMessages));
      }

      const body = (await response.json()) as { account: Account };
      setAccounts((current) =>
        current.map((candidate) =>
          candidate.id === body.account.id ? body.account : candidate
        )
      );
      if (selectedAccountId === account.id) {
        setSelectedAccountId('');
        setTransactions([]);
        setBankImportCsvText('');
        setBankImportPreview(null);
      }
      setShowArchivedAccounts(true);
      await loadDashboard(account.currency, dashboardPeriod);
      reportSuccess();
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function restoreAccount(account: Account) {
    const confirmed = window.confirm(
      `${t.restoreAccount}: ${account.name}\n\n${t.restoreAccountHint}`
    );
    if (!confirmed) return;

    clearNotice();
    setBusy(true);
    try {
      const response = await fetch(`/api/accounts/${account.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ archived: false })
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, apiErrorMessages));
      }

      const body = (await response.json()) as { account: Account };
      setAccounts((current) =>
        current.map((candidate) =>
          candidate.id === body.account.id ? body.account : candidate
        )
      );
      setSelectedAccountId(body.account.id);
      setShowArchivedAccounts(false);
      await loadDashboard(body.account.currency, dashboardPeriod);
      reportSuccess();
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function readBankCsvFile(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const utf8 = new TextDecoder('utf-8').decode(buffer);
    if (/北國銀行|取引日/.test(utf8)) {
      return utf8;
    }

    try {
      return new TextDecoder('shift_jis').decode(buffer);
    } catch {
      return utf8;
    }
  }

  async function handleBankCsvFileChange(
    event: ChangeEvent<HTMLInputElement>
  ) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    clearNotice();
    setBankImportPreview(null);
    setBankImportApplyReconcile(false);
    try {
      setBankImportCsvText(await readBankCsvFile(file));
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    }
  }

  async function previewBankImport() {
    if (!selectedAccountId || bankImportCsvText.trim().length === 0) return;

    setBusy(true);
    clearNotice();
    try {
      const response = await fetch(
        `/api/accounts/${selectedAccountId}/import/bank-statement/preview`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ csvText: bankImportCsvText })
        }
      );
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      const preview = (await response.json()) as BankImportPreviewResponse;
      setBankImportPreview(preview);
      setBankImportApplyReconcile(preview.balanceReconcile.canApplyReconcile);
      setBankImportApplyInitial(false);
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  async function commitBankImport() {
    if (!selectedAccountId || bankImportCsvText.trim().length === 0) return;

    setBusy(true);
    clearNotice();
    try {
      const response = await fetch(
        `/api/accounts/${selectedAccountId}/import/bank-statement/commit`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            csvText: bankImportCsvText,
            adapterId: bankImportPreview?.adapterId,
            applySuggestedInitialBalance: bankImportApplyReconcile
              ? false
              : bankImportApplyInitial,
            applyStatementEndReconcile: bankImportApplyReconcile
          })
        }
      );
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      const body = (await response.json()) as {
        imported: number;
        skippedDuplicate: number;
        reconcileApplied?: boolean;
        reconcileAdjustment?: number;
      };

      const accountsResponse = await fetch('/api/accounts');
      if (accountsResponse.ok) {
        const accountsBody = (await accountsResponse.json()) as {
          accounts: Account[];
        };
        setAccounts(accountsBody.accounts);
      }

      const transactionsResponse = await fetch(
        `/api/accounts/${selectedAccountId}/transactions`
      );
      if (transactionsResponse.ok) {
        const transactionsBody = (await transactionsResponse.json()) as {
          transactions: LedgerTransaction[];
        };
        setTransactions(transactionsBody.transactions);
      }

      if (selectedAccount) {
        await loadDashboard(selectedAccount.currency, dashboardPeriod);
      }

      setBankImportPreview(null);
      setBankImportCsvText('');
      setBankImportApplyInitial(false);
      setBankImportApplyReconcile(false);
      const reconcileNote =
        body.reconcileApplied && typeof body.reconcileAdjustment === 'number'
          ? ` · ${t.importBankReconcileDelta} ${formatMoney(
              body.reconcileAdjustment,
              'JPY',
              language
            )}`
          : '';
      reportSuccess(
        `${t.importBankCommit}: ${body.imported} (${t.importBankSummaryDuplicate} ${body.skippedDuplicate})${reconcileNote}`
      );
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  function bankAdapterLabel(adapterId: BankImportPreviewResponse['adapterId']) {
    return adapterId === 'hokoku' ? t.importBankAdapterHokoku : t.importBankAdapterSbi;
  }

  async function createManualTransaction(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    clearNotice();

    const fallbackAccountId = visibleAccounts.some(
      (account) => account.id === selectedAccountId
    )
      ? selectedAccountId
      : '';
    const formAccountId = visibleAccounts.some(
      (account) => account.id === transactionForm.accountId
    )
      ? transactionForm.accountId
      : '';
    const accountId = formAccountId || fallbackAccountId;
    const isTransfer = transactionForm.type === 'manual_transfer';
    const payload = isTransfer
      ? {
          type: transactionForm.type,
          fromAccountId: transactionForm.fromAccountId,
          toAccountId: transactionForm.toAccountId,
          amount: toInteger(transactionForm.amount),
          localDate: transactionForm.localDate,
          note: optionalNote(transactionForm.note)
        }
      : {
          type: transactionForm.type,
          accountId,
          amount: toInteger(transactionForm.amount),
          localDate: transactionForm.localDate,
          note: optionalNote(transactionForm.note)
        };

    try {
      const response = await fetch('/api/transactions/manual', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await readApiError(response, apiErrorMessages));
      setTransactionForm((current) => ({
        ...emptyTransactionForm(),
        accountId: current.accountId,
        fromAccountId: current.fromAccountId,
        toAccountId: current.toAccountId
      }));
      reportSuccess();
      await loadAccounts();
      await loadDashboard(dashboardCurrency, dashboardPeriod);
      if (selectedAccountId) {
        const history = await fetch(
          `/api/accounts/${selectedAccountId}/transactions`
        );
        if (history.ok) {
          const body = (await history.json()) as {
            transactions: LedgerTransaction[];
          };
          setTransactions(body.transactions);
        }
      }
    } catch (error) {
      reportError(error instanceof Error ? error.message : t.requestFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">Moneyflow</p>
          <h1>{t.familyFinance}</h1>
        </div>
        <div className="topbar-actions">
          {session ? (
            <>
              <button
                aria-current={activeTab === 'settings' ? 'page' : undefined}
                className="topbar-settings-button"
                onClick={() => setActiveTab('settings')}
                type="button"
              >
                {t.navSettings}
              </button>
              <div className="session-chip">
                <span>{session.user.email}</span>
                <strong>{memberRoleLabel(session.member.role, t)}</strong>
              </div>
            </>
          ) : null}
        </div>
      </header>

      {notice ? (
        <p
          className={`status-notice status-notice--${noticeKind}`}
          role="status"
        >
          {notice}
        </p>
      ) : null}

      {authState === 'checking' ? (
        <section className="loading-panel" aria-live="polite">
          <p className="eyebrow">{t.workspace}</p>
          <h2>{t.checking}</h2>
        </section>
      ) : null}

      {authState === 'signed-out' ? (
        <section className="login-panel">
          <div>
            <h2>{t.workspace}</h2>
            <p>{t.loginPrompt}</p>
          </div>
          <a className="primary-button" href="/api/auth/google/start">
            {t.googleLogin}
          </a>
        </section>
      ) : null}

      {authState === 'signed-in' ? (
        <section className="signed-in-stack">
          <AppTabNavigation
            activeTab={activeTab}
            navigationLabel={t.mainNavigation}
            onSelect={setActiveTab}
            placement="top"
            tabs={appTabs}
          />

          {activeTab === 'settings' ? (
            <section className="panel stack settings-panel">
              <div className="settings-general">
                <div className="section-heading">
                  <h2>{t.settingsTitle}</h2>
                </div>
                <SegmentedControl
                  label={t.languageControl}
                  options={[
                    { value: 'ko', label: '\ud55c\uad6d\uc5b4' },
                    { value: 'ja', label: '\u65e5\u672c\u8a9e' }
                  ]}
                  value={language}
                  onChange={(value) => void changeLanguage(value as Language)}
                />
                <button
                  className="logout-button"
                  disabled={busy}
                  onClick={() => void logout()}
                  type="button"
                >
                  {t.logout}
                </button>
              </div>
              {isOwner ? (
                <div className="stack member-management">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">{t.workspace}</p>
                  <h2>{t.members}</h2>
                </div>
              </div>
              <form className="compact-form" onSubmit={createWorkspaceMemberRecord}>
                <div className="field-grid three">
                  <label>
                    {t.memberEmail}
                    <input
                      required
                      type="email"
                      value={memberForm.email}
                      onChange={(event) =>
                        setMemberForm({ ...memberForm, email: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    {t.memberRole}
                    <select
                      value={memberForm.role}
                      onChange={(event) =>
                        setMemberForm({
                          ...memberForm,
                          role: event.target.value as MemberFormState['role']
                        })
                      }
                    >
                      <option value="editor">{t.roleEditor}</option>
                      <option value="viewer">{t.roleViewer}</option>
                    </select>
                  </label>
                  <label className="align-end">
                    <span className="sr-only">{t.addMember}</span>
                    <button className="primary-button" disabled={busy} type="submit">
                      {t.addMember}
                    </button>
                  </label>
                </div>
              </form>
              {workspaceMembers.length === 0 ? (
                <p className="panel-message">{t.membersEmpty}</p>
              ) : (
                <ul className="member-list">
                  {workspaceMembers.map((workspaceMember) => (
                    <li key={workspaceMember.id} className="member-row">
                      <div>
                        <strong>{workspaceMember.email}</strong>
                        <span>
                          {memberRoleLabel(workspaceMember.role, t)} ·{' '}
                          {memberStatusLabel(workspaceMember.status, t)}
                        </span>
                      </div>
                      {workspaceMember.status === 'active' &&
                      workspaceMember.id !== session?.member.id ? (
                        <button
                          className="ghost-button"
                          disabled={busy}
                          type="button"
                          onClick={() => void disableWorkspaceMember(workspaceMember.id)}
                        >
                          {t.disableMember}
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              <div className="section-heading">
                <div>
                  <p className="eyebrow">{t.workspace}</p>
                  <h2>{t.exportDownload}</h2>
                </div>
              </div>
              <div className="stack export-controls">
                <a
                  className="primary-button"
                  download
                  href="/api/export/workspace"
                >
                  {t.exportWorkspace}
                </a>
                <div className="field-grid export-account-csv">
                  <label>
                    {t.exportAccountSelect}
                    <select
                      disabled={visibleAccounts.length === 0}
                      value={selectedAccountId}
                      onChange={(event) => setSelectedAccountId(event.target.value)}
                    >
                      {visibleAccounts.length === 0 ? (
                        <option value="">{t.selectPlaceholder}</option>
                      ) : (
                        visibleAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                  {selectedAccountId ? (
                    <a
                      className="primary-button align-end"
                      download
                      href={`/api/export/accounts/${selectedAccountId}/transactions.csv`}
                    >
                      {t.exportAccountCsv}
                    </a>
                  ) : (
                    <span className="primary-button align-end" aria-disabled="true">
                      {t.exportAccountCsv}
                    </span>
                  )}
                </div>
              </div>
                </div>
              ) : null}
            </section>
          ) : null}

          {activeTab === 'dashboard' ? (
          <section className="panel stack dashboard-overview">
            <div className="section-heading">
              <div>
                <p className="eyebrow">{t.dashboard}</p>
                <h2>
                  {countryLabel(language, country)} - {dashboardCurrency}
                </h2>
              </div>
              <div className="dashboard-controls">
                <SegmentedControl
                  label={t.countryControl}
                  options={countries.map((value) => ({
                    value,
                    label: countryLabel(language, value)
                  }))}
                  value={country}
                  onChange={(value) => setCountry(value as Country)}
                />
                <SegmentedControl
                  label={t.period}
                  options={dashboardPeriods.map((value) => ({
                    value,
                    label: periodLabel(language, value)
                  }))}
                  value={dashboardPeriod}
                  onChange={(value) =>
                    setDashboardPeriod(value as DashboardPeriod)
                  }
                />
              </div>
            </div>

            <div className="metric-grid" aria-busy={dashboardLoading}>
              <MetricCard
                label={t.currentBalance}
                value={formatDashboardMoney(
                  displayedDashboard?.currentBalance,
                  dashboardCurrency,
                  language
                )}
              />
              <MetricCard
                label={t.periodInflow}
                value={formatDashboardMoney(
                  displayedDashboard?.periodInflow,
                  dashboardCurrency,
                  language
                )}
              />
              <MetricCard
                label={t.periodOutflow}
                value={formatDashboardMoney(
                  displayedDashboard?.periodOutflow,
                  dashboardCurrency,
                  language
                )}
              />
              <MetricCard
                label={t.lowestExpectedBalance}
                value={formatDashboardMoney(
                  displayedDashboard?.lowestExpectedBalance,
                  dashboardCurrency,
                  language
                )}
              />
              <MetricCard
                label={t.safetyThreshold}
                value={formatDashboardMoney(
                  displayedDashboard?.safetyThreshold,
                  dashboardCurrency,
                  language
                )}
              />
              <MetricCard
                label={t.availableBalance}
                value={formatDashboardMoney(
                  displayedDashboard?.availableBalance,
                  dashboardCurrency,
                  language
                )}
              />
            </div>
            {dashboardLoading ? (
              <p className="muted-status">{t.loadingDashboard}</p>
            ) : null}

            <div className="dashboard-detail-grid">
              <section className="summary-section">
                <h3>{t.activeAccounts}</h3>
                <div className="summary-list">
                  {displayedDashboard?.accounts.length ? (
                    displayedDashboard.accounts.map((account) => (
                      <div className="summary-row" key={account.id}>
                        <span>{account.name}</span>
                        <strong
                          className={
                            account.balance < 0 ? 'negative' : undefined
                          }
                          title={
                            account.balance < 0
                              ? t.negativeBalanceWarning
                              : undefined
                          }
                        >
                          {formatMoney(
                            account.balance,
                            account.currency,
                            language
                          )}
                        </strong>
                      </div>
                    ))
                  ) : (
                    <p className="panel-message">{t.emptyAccounts}</p>
                  )}
                </div>
              </section>

              <section className="summary-section forecast-section">
                <h3>{t.forecast}</h3>
                {displayedDashboard?.forecastRows.length ? (
                  <div className="forecast-table-scroll">
                    <div className="forecast-table">
                      <div className="forecast-row forecast-head">
                      <span>{t.date}</span>
                      <span>{t.inflow}</span>
                      <span>{t.outflow}</span>
                      <span>{t.balance}</span>
                      <span>{t.eventCount}</span>
                    </div>
                      {displayedDashboard.forecastRows.map((row) => {
                        const safetyThreshold =
                          displayedDashboard.safetyThreshold ?? 0;
                        const risky = isBelowSafetyThreshold(
                          row.closingBalance,
                          safetyThreshold
                        );
                        const negative = row.closingBalance < 0;

                        return (
                          <div
                            className={
                              risky
                                ? 'forecast-row risk-row'
                                : 'forecast-row'
                            }
                            key={row.date}
                            title={risky ? t.belowSafetyThreshold : undefined}
                          >
                            <span>{row.date}</span>
                            <span>
                              {formatMoney(
                                row.inflow,
                                dashboardCurrency,
                                language
                              )}
                            </span>
                            <span>
                              {formatMoney(
                                row.outflow,
                                dashboardCurrency,
                                language
                              )}
                            </span>
                            <strong
                              className={negative ? 'negative' : undefined}
                              title={
                                negative ? t.negativeBalanceWarning : undefined
                              }
                            >
                              {formatMoney(
                                row.closingBalance,
                                dashboardCurrency,
                                language
                              )}
                            </strong>
                            <span>
                              {row.events.length} {t.eventCount}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="panel-message">{t.forecastEmpty}</p>
                )}
              </section>
            </div>
          </section>
          ) : null}

          {activeTab === 'planned' ? (
          <section className="panel stack planned-cashflow">
            <div className="section-heading">
              <div>
                <p className="eyebrow">{t.forecast}</p>
                <h2>{t.plannedCashflow}</h2>
              </div>
            </div>

            <div className="planned-form-grid">
              <form className="compact-form" onSubmit={createIncomeOccurrence}>
                <h3>{t.plannedIncome}</h3>
                <div className="field-grid three">
                  <AccountSelect
                    accounts={visibleAccounts}
                    label={t.selectAccount}
                    placeholder={t.selectPlaceholder}
                    value={incomeOccurrenceForm.accountId || selectedAccountId}
                    onChange={(value) =>
                      setIncomeOccurrenceForm({
                        ...incomeOccurrenceForm,
                        accountId: value
                      })
                    }
                  />
                  <label>
                    {t.amount}
                    <input
                      inputMode="numeric"
                      required
                      value={incomeOccurrenceForm.amount}
                      onChange={(event) =>
                        setIncomeOccurrenceForm({
                          ...incomeOccurrenceForm,
                          amount: event.target.value
                        })
                      }
                    />
                  </label>
                  <label>
                    {t.expectedDate}
                    <input
                      required
                      type="date"
                      value={incomeOccurrenceForm.expectedDate}
                      onChange={(event) =>
                        setIncomeOccurrenceForm({
                          ...incomeOccurrenceForm,
                          expectedDate: event.target.value
                        })
                      }
                    />
                  </label>
                </div>
                <label>
                  {t.note}
                  <input
                    value={incomeOccurrenceForm.note}
                    onChange={(event) =>
                      setIncomeOccurrenceForm({
                        ...incomeOccurrenceForm,
                        note: event.target.value
                      })
                    }
                  />
                </label>
                <button className="primary-button" disabled={busy} type="submit">
                  {t.save}
                </button>
              </form>

              <form className="compact-form" onSubmit={createFixedExpense}>
                <h3>{t.fixedExpense}</h3>
                <div className="field-grid three">
                  <label>
                    {t.name}
                    <input
                      required
                      value={fixedExpenseForm.name}
                      onChange={(event) =>
                        setFixedExpenseForm({
                          ...fixedExpenseForm,
                          name: event.target.value
                        })
                      }
                    />
                  </label>
                  <AccountSelect
                    accounts={visibleAccounts}
                    label={t.selectAccount}
                    placeholder={t.selectPlaceholder}
                    value={fixedExpenseForm.accountId || selectedAccountId}
                    onChange={(value) =>
                      setFixedExpenseForm({
                        ...fixedExpenseForm,
                        accountId: value
                      })
                    }
                  />
                  <label>
                    {t.amount}
                    <input
                      inputMode="numeric"
                      required
                      value={fixedExpenseForm.amount}
                      onChange={(event) =>
                        setFixedExpenseForm({
                          ...fixedExpenseForm,
                          amount: event.target.value
                        })
                      }
                    />
                  </label>
                </div>
                <RecurringRuleFields
                  labels={t}
                  value={fixedExpenseForm.recurring}
                  onChange={(recurring) =>
                    setFixedExpenseForm({ ...fixedExpenseForm, recurring })
                  }
                />
                <fieldset className="ledger-source-fieldset">
                  <legend>{t.ledgerSource}</legend>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="fixed-expense-ledger-source"
                      checked={fixedExpenseForm.ledgerSource === 'bank'}
                      onChange={() =>
                        setFixedExpenseForm({
                          ...fixedExpenseForm,
                          ledgerSource: 'bank'
                        })
                      }
                    />
                    <span>
                      <strong>{t.ledgerSourceBank}</strong>
                      <small>{t.ledgerSourceBankHint}</small>
                    </span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="fixed-expense-ledger-source"
                      checked={fixedExpenseForm.ledgerSource === 'schedule'}
                      onChange={() =>
                        setFixedExpenseForm({
                          ...fixedExpenseForm,
                          ledgerSource: 'schedule'
                        })
                      }
                    />
                    <span>
                      <strong>{t.ledgerSourceSchedule}</strong>
                      <small>{t.ledgerSourceScheduleHint}</small>
                    </span>
                  </label>
                </fieldset>
                <button className="primary-button" disabled={busy} type="submit">
                  {t.save}
                </button>
              </form>

              <form className="compact-form" onSubmit={createScheduledTransfer}>
                <h3>{t.scheduledTransfer}</h3>
                <div className="field-grid three">
                  <label>
                    {t.name}
                    <input
                      required
                      value={scheduledTransferForm.name}
                      onChange={(event) =>
                        setScheduledTransferForm({
                          ...scheduledTransferForm,
                          name: event.target.value
                        })
                      }
                    />
                  </label>
                  <AccountSelect
                    accounts={visibleAccounts}
                    label={t.fromAccount}
                    placeholder={t.selectPlaceholder}
                    value={scheduledTransferForm.fromAccountId}
                    onChange={(value) =>
                      setScheduledTransferForm({
                        ...scheduledTransferForm,
                        fromAccountId: value
                      })
                    }
                  />
                  <AccountSelect
                    accounts={visibleAccounts}
                    label={t.transferTarget}
                    placeholder={t.selectPlaceholder}
                    value={scheduledTransferForm.toAccountId}
                    onChange={(value) =>
                      setScheduledTransferForm({
                        ...scheduledTransferForm,
                        toAccountId: value
                      })
                    }
                  />
                </div>
                <div className="field-grid">
                  <label>
                    {t.amount}
                    <input
                      inputMode="numeric"
                      required
                      value={scheduledTransferForm.amount}
                      onChange={(event) =>
                        setScheduledTransferForm({
                          ...scheduledTransferForm,
                          amount: event.target.value
                        })
                      }
                    />
                  </label>
                </div>
                <RecurringRuleFields
                  labels={t}
                  value={scheduledTransferForm.recurring}
                  onChange={(recurring) =>
                    setScheduledTransferForm({
                      ...scheduledTransferForm,
                      recurring
                    })
                  }
                />
                <button className="primary-button" disabled={busy} type="submit">
                  {t.save}
                </button>
              </form>
            </div>

            <div className="planned-list-grid">
              <PlannedList title={t.plannedIncome} empty={t.plannedCashflowEmpty}>
                {visibleIncomeOccurrences.map((occurrence) => (
                  <div className="planned-row" key={occurrence.id}>
                    <span>
                      <strong>{occurrence.expectedDate}</strong>
                      <small>
                        {occurrence.note ??
                          scheduleStatusLabel(language, occurrence.status)}
                      </small>
                    </span>
                    <span className="positive">
                      {formatMoney(
                        occurrence.amount,
                        occurrence.currency,
                        language
                      )}
                    </span>
                  </div>
                ))}
              </PlannedList>
              <PlannedList title={t.fixedExpense} empty={t.plannedCashflowEmpty}>
                {visibleFixedExpenses.map((expense) => (
                  <div className="planned-row" key={expense.id}>
                    <span>
                      <strong>{expense.name}</strong>
                      <small>{expense.isActive ? t.active : t.inactive}</small>
                    </span>
                    <span className="planned-row-side">
                      <span className="negative">
                        {formatMoney(-expense.amount, expense.currency, language)}
                      </span>
                      {canEditLedger && expense.isActive ? (
                        <button
                          className="secondary-button planned-row-cta"
                          disabled={busy}
                          onClick={() => void disableFixedExpense(expense)}
                          type="button"
                        >
                          {t.disableFixedExpense}
                        </button>
                      ) : null}
                    </span>
                  </div>
                ))}
              </PlannedList>
              <PlannedList
                title={t.scheduledTransfer}
                empty={t.plannedCashflowEmpty}
              >
                {visibleScheduledTransfers.map((transfer) => (
                  <div className="planned-row" key={transfer.id}>
                    <span>
                      <strong>{transfer.name}</strong>
                      <small>{transfer.isActive ? t.active : t.inactive}</small>
                    </span>
                    <span className="negative">
                      {formatMoney(-transfer.amount, transfer.currency, language)}
                    </span>
                  </div>
                ))}
              </PlannedList>
            </div>
          </section>
          ) : null}

          {activeTab === 'outflow' ? (
          <section className="panel stack scheduled-outflow">
            <div className="section-heading scheduled-outflow-tabs">
              <div>
                <p className="eyebrow">{t.outflow}</p>
                <h2>{t.scheduledOutflow}</h2>
                <p className="muted-status">
                  {t.cards}: {t.markBillingPaid} · {t.loans}: {t.markLoanPaid}
                </p>
              </div>
              <SegmentedControl
                label={t.scheduledOutflow}
                options={[
                  { value: 'cards', label: t.cards },
                  { value: 'loans', label: t.loans }
                ]}
                value={scheduledOutflowTab}
                onChange={(value) =>
                  setScheduledOutflowTab(value as ScheduledOutflowTab)
                }
              />
            </div>

            {scheduledOutflowTab === 'cards' ? (
              <div className="scheduled-outflow-panel">
                <div className="scheduled-outflow-forms">
                  <form className="compact-form" onSubmit={createCardRecord}>
                    <h3>{t.cards}</h3>
                    <div className="field-grid">
                      <label>
                        {t.name}
                        <input
                          required
                          value={cardForm.name}
                          onChange={(event) =>
                            setCardForm({ ...cardForm, name: event.target.value })
                          }
                        />
                      </label>
                      <label>
                        {t.ownership}
                        <select
                          value={cardForm.ownershipType}
                          onChange={(event) =>
                            setCardForm({
                              ...cardForm,
                              ownershipType: event.target.value as OwnershipType
                            })
                          }
                        >
                          {ownershipTypes.map((value) => (
                            <option key={value} value={value}>
                              {ownershipLabel(language, value)}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="field-grid three">
                      <AccountSelect
                        accounts={visibleAccounts}
                        label={t.selectAccount}
                        placeholder={t.selectPlaceholder}
                        value={cardForm.paymentAccountId || selectedAccountId}
                        onChange={(value) =>
                          setCardForm({ ...cardForm, paymentAccountId: value })
                        }
                      />
                      <label>
                        {t.paymentDay}
                        <input
                          inputMode="numeric"
                          max="31"
                          min="1"
                          required
                          value={cardForm.paymentDay}
                          onChange={(event) =>
                            setCardForm({
                              ...cardForm,
                              paymentDay: event.target.value
                            })
                          }
                        />
                      </label>
                    </div>
                    <button
                      className="primary-button"
                      disabled={busy}
                      type="submit"
                    >
                      {t.save}
                    </button>
                  </form>

                  <form className="compact-form" onSubmit={createBillingCycle}>
                    <h3>{t.paymentMonth}</h3>
                    <div className="field-grid three">
                      <label>
                        {t.card}
                        <select
                          required
                          value={billingCycleForm.cardId}
                          onChange={(event) =>
                            setBillingCycleForm({
                              ...billingCycleForm,
                              cardId: event.target.value
                            })
                          }
                        >
                          <option value="">{t.selectPlaceholder}</option>
                          {visibleCards.map((card) => (
                            <option key={card.id} value={card.id}>
                              {card.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        {t.paymentMonth}
                        <input
                          required
                          type="month"
                          value={billingCycleForm.paymentMonth}
                          onChange={(event) =>
                            setBillingCycleForm({
                              ...billingCycleForm,
                              paymentMonth: event.target.value
                            })
                          }
                        />
                      </label>
                      <label>
                        {t.paymentDueDate}
                        <input
                          required
                          type="date"
                          value={billingCycleForm.paymentDueDate}
                          onChange={(event) =>
                            setBillingCycleForm({
                              ...billingCycleForm,
                              paymentDueDate: event.target.value
                            })
                          }
                        />
                      </label>
                    </div>
                    <div className="field-grid">
                      <label>
                        {t.amount}
                        <input
                          inputMode="numeric"
                          required
                          value={billingCycleForm.amount}
                          onChange={(event) =>
                            setBillingCycleForm({
                              ...billingCycleForm,
                              amount: event.target.value
                            })
                          }
                        />
                      </label>
                      <label>
                        {t.statementMonth}
                        <input
                          type="month"
                          value={billingCycleForm.statementMonth}
                          onChange={(event) =>
                            setBillingCycleForm({
                              ...billingCycleForm,
                              statementMonth: event.target.value
                            })
                          }
                        />
                      </label>
                    </div>
                    <button
                      className="primary-button"
                      disabled={busy}
                      type="submit"
                    >
                      {t.save}
                    </button>
                  </form>
                </div>

                <div className="planned-list-grid">
                  <PlannedList title={t.cards} empty={t.scheduledOutflowEmpty}>
                    {visibleCards.map((card) => (
                      <div className="planned-row" key={card.id}>
                        <span>
                          <strong>{card.name}</strong>
                          <small>
                            {ownershipLabel(language, card.ownershipType)} -{' '}
                            {t.paymentDay} {card.paymentDay} -{' '}
                            {card.isActive ? t.active : t.inactive}
                          </small>
                        </span>
                        <span>{card.currency}</span>
                      </div>
                    ))}
                  </PlannedList>
                  <PlannedList
                    title={t.paymentMonth}
                    empty={t.scheduledOutflowEmpty}
                  >
                    {visibleBillingCycles.map((cycle) => (
                      <div className="planned-row" key={cycle.id}>
                        <span>
                          <strong>
                            {cardNameById.get(cycle.cardId) ?? cycle.cardId}
                          </strong>
                          <small>
                            {cycle.paymentMonth} - {cycle.paymentDueDate} -{' '}
                            {scheduleStatusLabel(language, cycle.status)}
                          </small>
                        </span>
                        <span className="planned-row-actions">
                          <span className="negative">
                            {formatMoney(
                              -cycle.amount,
                              cycle.currency,
                              language
                            )}
                          </span>
                          {canMarkBillingCyclePaid(cycle.status) ? (
                            <button
                              className="primary-button"
                              disabled={busy}
                              onClick={() => void markBillingCyclePaid(cycle.id)}
                              type="button"
                            >
                              {t.markBillingPaid}
                            </button>
                          ) : null}
                        </span>
                      </div>
                    ))}
                  </PlannedList>
                </div>
              </div>
            ) : (
              <div className="scheduled-outflow-panel">
                <form className="compact-form" onSubmit={createLoanRecord}>
                  <h3>{t.loans}</h3>
                  <div className="field-grid three">
                    <label>
                      {t.name}
                      <input
                        required
                        value={loanForm.name}
                        onChange={(event) =>
                          setLoanForm({ ...loanForm, name: event.target.value })
                        }
                      />
                    </label>
                    <label>
                      {t.principalAmount}
                      <input
                        inputMode="numeric"
                        required
                        value={loanForm.principalAmount}
                        onChange={(event) =>
                          setLoanForm({
                            ...loanForm,
                            principalAmount: event.target.value
                          })
                        }
                      />
                    </label>
                    <label>
                      {t.annualInterestRate}
                      <input
                        inputMode="decimal"
                        required
                        step="0.0001"
                        value={loanForm.annualInterestRate}
                        onChange={(event) =>
                          setLoanForm({
                            ...loanForm,
                            annualInterestRate: event.target.value
                          })
                        }
                      />
                    </label>
                  </div>
                  <div className="field-grid three">
                    <label>
                      {t.startDate}
                      <input
                        required
                        type="date"
                        value={loanForm.startDate}
                        onChange={(event) =>
                          setLoanForm({
                            ...loanForm,
                            startDate: event.target.value
                          })
                        }
                      />
                    </label>
                    <label>
                      {t.termYears}
                      <input
                        inputMode="numeric"
                        required
                        value={loanForm.termYears}
                        onChange={(event) =>
                          setLoanForm({
                            ...loanForm,
                            termYears: event.target.value
                          })
                        }
                      />
                    </label>
                    <label>
                      {t.paymentDay}
                      <input
                        inputMode="numeric"
                        max="31"
                        min="1"
                        required
                        value={loanForm.paymentDay}
                        onChange={(event) =>
                          setLoanForm({
                            ...loanForm,
                            paymentDay: event.target.value
                          })
                        }
                      />
                    </label>
                  </div>
                  <AccountSelect
                    accounts={visibleAccounts}
                    label={t.selectAccount}
                    placeholder={t.selectPlaceholder}
                    value={loanForm.paymentAccountId || selectedAccountId}
                    onChange={(value) =>
                      setLoanForm({ ...loanForm, paymentAccountId: value })
                    }
                  />
                  <button
                    className="primary-button"
                    disabled={busy}
                    type="submit"
                  >
                    {t.save}
                  </button>
                </form>

                <div className="planned-list-grid">
                  <PlannedList title={t.loans} empty={t.scheduledOutflowEmpty}>
                    {visibleLoans.map((loan) => (
                      <div className="planned-row" key={loan.id}>
                        <span>
                          <strong>{loan.name}</strong>
                          <small>
                            {loan.termYears}
                            {t.yearSuffix} -{' '}
                            {t.paymentDay} {loan.paymentDay} -{' '}
                            {loan.isActive ? t.active : t.inactive}
                          </small>
                        </span>
                        <span className="negative">
                          {formatMoney(
                            -loan.principalAmount,
                            loan.currency,
                            language
                          )}
                        </span>
                      </div>
                    ))}
                  </PlannedList>
                  <PlannedList
                    title={t.paymentDueDate}
                    empty={t.scheduledOutflowEmpty}
                  >
                    {visibleLoanSchedules
                      .filter((schedule) => loanNameById.has(schedule.loanId))
                      .map((schedule) => {
                      const loan = visibleLoans.find(
                        (candidate) => candidate.id === schedule.loanId
                      )!;

                      return (
                        <div className="planned-row" key={schedule.id}>
                          <span>
                            <strong>
                              {loanNameById.get(schedule.loanId) ?? schedule.loanId}
                            </strong>
                            <small>
                              #{schedule.paymentNumber} - {schedule.dueDate} -{' '}
                              {scheduleStatusLabel(language, schedule.status)}
                            </small>
                          </span>
                          <span className="planned-row-actions">
                            <span className="negative">
                              {formatMoney(
                                -schedule.paymentAmount,
                                loan.currency,
                                language
                              )}
                            </span>
                            {canMarkLoanSchedulePaid(schedule.status) ? (
                              <button
                                className="primary-button"
                                disabled={busy}
                                onClick={() =>
                                  void markLoanSchedulePaid(schedule.id)
                                }
                                type="button"
                              >
                                {t.markLoanPaid}
                              </button>
                            ) : null}
                          </span>
                        </div>
                      );
                    })}
                  </PlannedList>
                </div>
              </div>
            )}
          </section>
          ) : null}

          {activeTab === 'accounts' ? (
          <section className="dashboard-grid">
          <aside className="panel stack">
            <div className="section-heading">
              <div>
                <p className="eyebrow">{t.dashboard}</p>
                <h2>
                  {countryLabel(language, country)} {t.accounts}
                </h2>
              </div>
              <SegmentedControl
                label={t.countryControl}
                options={countries.map((value) => ({
                  value,
                  label: countryLabel(language, value)
                }))}
                value={country}
                onChange={(value) => setCountry(value as Country)}
              />
            </div>

            <div className="balance-strip">
              <span>{t.currentBalance}</span>
              <strong>
                {formatDashboardMoney(
                  displayedDashboard?.currentBalance,
                  dashboardCurrency,
                  language
                )}
              </strong>
            </div>

            <div className="account-list">
              {visibleAccounts.length === 0 ? (
                <p className="panel-message">{t.emptyAccounts}</p>
              ) : (
                visibleAccounts.map((account) => (
                  <button
                    className={`account-row ${
                      account.id === selectedAccountId ? 'selected' : ''
                    }`}
                    key={account.id}
                    onClick={() => setSelectedAccountId(account.id)}
                    type="button"
                  >
                    <span>
                      <strong>{account.name}</strong>
                      <small>
                        {accountTypeLabel(language, account.accountType)} -{' '}
                        {ownershipLabel(language, account.ownershipType)}
                      </small>
                    </span>
                    <span className="account-balance">
                      {formatMoney(account.balance, account.currency, language)}
                    </span>
                    <span className="status-line">
                      {account.includeInDashboard
                        ? t.dashboardOn
                        : t.dashboardOff}
                      {' - '}
                      {account.isActive ? t.active : t.inactive}
                    </span>
                  </button>
                ))
              )}
            </div>

            {archivedAccounts.length > 0 ? (
              <div className="archived-accounts-panel">
                <button
                  className="archived-accounts-toggle"
                  disabled={busy}
                  onClick={() => setShowArchivedAccounts((current) => !current)}
                  type="button"
                >
                  {showArchivedAccounts
                    ? t.hideArchivedAccounts
                    : t.showArchivedAccounts}{' '}
                  ({archivedAccounts.length})
                </button>
                {showArchivedAccounts ? (
                  <div className="stack archived-accounts-section">
                    <p className="eyebrow">{t.archivedAccounts}</p>
                    <div className="account-list">
                      {archivedAccounts.map((account) => (
                        <div className="archived-account-row" key={account.id}>
                          <span>
                            <strong>{account.name}</strong>
                            <small>
                              {accountTypeLabel(language, account.accountType)} -{' '}
                              {ownershipLabel(language, account.ownershipType)}
                            </small>
                            <span className="status-line">
                              {account.archivedAt
                                ? new Date(account.archivedAt).toLocaleDateString(
                                    language === 'ja' ? 'ja-JP' : 'ko-KR'
                                  )
                                : ''}
                            </span>
                          </span>
                          <span className="account-balance">
                            {formatMoney(
                              account.balance,
                              account.currency,
                              language
                            )}
                          </span>
                          {canEditLedger ? (
                            <button
                              className="restore-account-button"
                              disabled={busy}
                              onClick={() => void restoreAccount(account)}
                              type="button"
                            >
                              {t.restoreAccount}
                            </button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
            {selectedAccountId && recurringSuggestions.length > 0 ? (
              <div className="archived-accounts-panel recurring-suggestions-panel">
                <button
                  className="archived-accounts-toggle"
                  disabled={busy}
                  onClick={() =>
                    setShowRecurringSuggestions((current) => !current)
                  }
                  type="button"
                >
                  {showRecurringSuggestions
                    ? t.hideRecurringSuggestions
                    : t.showRecurringSuggestions}{' '}
                  ({recurringSuggestions.length})
                </button>
                {showRecurringSuggestions ? (
                  <div className="stack recurring-suggestions-section">
                    <p className="eyebrow">{t.recurringSuggestions}</p>
                    <div className="recurring-suggestion-list">
                      {recurringSuggestions.map((suggestion) => (
                        <div
                          className="recurring-suggestion-card"
                          key={suggestion.id}
                        >
                          <div className="recurring-suggestion-main">
                            <strong>{suggestion.label}</strong>
                            <small>
                              {formatMoney(
                                -suggestion.amount,
                                suggestion.currency,
                                language
                              )}{' '}
                              · {t.recurringSuggestionOccurrences}{' '}
                              {suggestion.occurrenceCount} ·{' '}
                              {t.recurringSuggestionConfidence}{' '}
                              {Math.round(suggestion.confidence * 100)}%
                            </small>
                          </div>
                          <div className="recurring-suggestion-actions">
                            {suggestion.alreadyRegistered ? (
                              <span className="status-line">
                                {t.recurringSuggestionRegistered}
                              </span>
                            ) : canEditLedger ? (
                              <>
                                <button
                                  className="primary-button"
                                  disabled={busy}
                                  onClick={() =>
                                    void registerRecurringSuggestion(suggestion)
                                  }
                                  type="button"
                                >
                                  {t.recurringSuggestionRegister}
                                </button>
                                <button
                                  className="secondary-button"
                                  disabled={busy}
                                  onClick={() =>
                                    void dismissRecurringSuggestionItem(
                                      suggestion.id
                                    )
                                  }
                                  type="button"
                                >
                                  {t.recurringSuggestionDismiss}
                                </button>
                              </>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </aside>

          <section className="panel stack">
            <div className="section-heading">
              <div>
                <p className="eyebrow">{t.newAccount}</p>
                <h2>{t.createAccount}</h2>
              </div>
            </div>
            <form className="compact-form" onSubmit={createAccount}>
              <label>
                {t.name}
                <input
                  required
                  value={accountForm.name}
                  onChange={(event) =>
                    setAccountForm({ ...accountForm, name: event.target.value })
                  }
                />
              </label>
              <div className="field-grid">
                <label>
                  {t.labelCountry}
                  <select
                    value={accountForm.country}
                    onChange={(event) =>
                      setAccountForm({
                        ...accountForm,
                        country: event.target.value as Country
                      })
                    }
                  >
                    {countries.map((value) => (
                      <option key={value} value={value}>
                        {countryLabel(language, value)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t.labelAccountType}
                  <select
                    value={accountForm.accountType}
                    onChange={(event) =>
                      setAccountForm({
                        ...accountForm,
                        accountType: event.target.value as AccountType
                      })
                    }
                  >
                    {accountTypes.map((value) => (
                      <option key={value} value={value}>
                        {accountTypeLabel(language, value)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="field-grid">
                <label>
                  {t.ownership}
                  <select
                    value={accountForm.ownershipType}
                    onChange={(event) =>
                      setAccountForm({
                        ...accountForm,
                        ownershipType: event.target.value as OwnershipType
                      })
                    }
                  >
                    {ownershipTypes.map((value) => (
                      <option key={value} value={value}>
                        {ownershipLabel(language, value)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t.initialBalance}
                  <input
                    inputMode="numeric"
                    required
                    value={accountForm.initialBalance}
                    onChange={(event) =>
                      setAccountForm({
                        ...accountForm,
                        initialBalance: event.target.value
                      })
                    }
                  />
                </label>
              </div>
              <div className="toggle-grid">
                <label>
                  <input
                    checked={accountForm.includeInDashboard}
                    onChange={(event) =>
                      setAccountForm({
                        ...accountForm,
                        includeInDashboard: event.target.checked
                      })
                    }
                    type="checkbox"
                  />
                  {t.dashboardOn}
                </label>
                <label>
                  <input
                    checked={accountForm.allowNegativeBalance}
                    onChange={(event) =>
                      setAccountForm({
                        ...accountForm,
                        allowNegativeBalance: event.target.checked
                      })
                    }
                    type="checkbox"
                  />
                  {t.allowNegativeBalance}
                </label>
              </div>
              <label>
                {t.safetyThreshold}
                <input
                  inputMode="numeric"
                  value={accountForm.safetyThreshold}
                  onChange={(event) =>
                    setAccountForm({
                      ...accountForm,
                      safetyThreshold: event.target.value
                    })
                  }
                />
              </label>
              {accountUsesBankCsvLink(
                accountForm.country,
                accountForm.accountType
              ) ? (
                <label>
                  {t.bankAccountNumber}
                  <input
                    inputMode="numeric"
                    placeholder="0019609"
                    value={accountForm.bankAccountNumber}
                    onChange={(event) =>
                      setAccountForm({
                        ...accountForm,
                        bankAccountNumber: event.target.value
                      })
                    }
                  />
                  <span className="field-hint">{t.bankAccountNumberHint}</span>
                </label>
              ) : null}
              <button className="primary-button" disabled={busy} type="submit">
                {t.save}
              </button>
            </form>
          </section>

          <section className="panel stack wide-panel">
            <div className="section-heading">
              <div>
                <p className="eyebrow">{t.transactions}</p>
                <h2>{selectedAccount?.name ?? t.selectAccount}</h2>
              </div>
            </div>

            {selectedAccount ? (
              <div className="account-controls">
                <label>
                  <input
                    checked={selectedAccount.includeInDashboard}
                    onChange={(event) =>
                      void patchAccount(selectedAccount, {
                        includeInDashboard: event.target.checked
                      })
                    }
                    type="checkbox"
                  />
                  {t.dashboardOn}
                </label>
                <label>
                  <input
                    checked={selectedAccount.isActive}
                    onChange={(event) =>
                      void patchAccount(selectedAccount, {
                        isActive: event.target.checked
                      })
                    }
                    type="checkbox"
                  />
                  {t.active}
                </label>
                {canEditLedger ? (
                  <button
                    className="archive-account-button"
                    disabled={busy}
                    onClick={() => void archiveAccount(selectedAccount)}
                    type="button"
                  >
                    {t.archiveAccount}
                  </button>
                ) : null}
              </div>
            ) : null}

            {selectedAccount &&
            accountUsesBankCsvLink(
              selectedAccount.country,
              selectedAccount.accountType
            ) &&
            canEditLedger ? (
              <div className="linked-bank-account-field">
                <label>
                  {t.bankAccountNumber}
                  <input
                    inputMode="numeric"
                    value={linkedBankAccountNumber}
                    onChange={(event) =>
                      setLinkedBankAccountNumber(event.target.value)
                    }
                  />
                  <span className="field-hint">{t.bankAccountNumberHint}</span>
                </label>
                <button
                  className="secondary-button"
                  disabled={
                    busy ||
                    linkedBankAccountNumber.trim() ===
                      (selectedAccount.bankAccountNumber ?? '')
                  }
                  onClick={() =>
                    void patchAccount(selectedAccount, {
                      bankAccountNumber:
                        linkedBankAccountNumber.trim().length > 0
                          ? linkedBankAccountNumber.trim()
                          : null
                    })
                  }
                  type="button"
                >
                  {t.saveBankAccountNumber}
                </button>
              </div>
            ) : null}

            {selectedAccount &&
            selectedAccount.country === 'JP' &&
            selectedAccount.currency === 'JPY' &&
            canEditLedger ? (
              <div className="stack bank-import-panel">
                <h3>{t.importBankCsv}</h3>
                <label className="file-input-label">
                  {t.importBankCsvSelect}
                  <input
                    accept=".csv,text/csv"
                    onChange={(event) => void handleBankCsvFileChange(event)}
                    type="file"
                  />
                </label>
                <div className="button-row">
                  <button
                    className="secondary-button"
                    disabled={busy || bankImportCsvText.trim().length === 0}
                    onClick={() => void previewBankImport()}
                    type="button"
                  >
                    {t.importBankPreview}
                  </button>
                  <button
                    className="primary-button"
                    disabled={
                      busy ||
                      bankImportCsvText.trim().length === 0 ||
                      !bankImportPreview
                    }
                    onClick={() => void commitBankImport()}
                    type="button"
                  >
                    {t.importBankCommit}
                  </button>
                </div>
                {bankImportPreview ? (
                  <div className="bank-import-summary">
                    <p>
                      <strong>{bankAdapterLabel(bankImportPreview.adapterId)}</strong>
                      {bankImportPreview.meta.accountNumber
                        ? ` · ${bankImportPreview.meta.accountNumber}`
                        : null}
                    </p>
                    <p>
                      {t.importBankSummaryNew}: {bankImportPreview.summary.new} ·{' '}
                      {t.importBankSummaryDuplicate}:{' '}
                      {bankImportPreview.summary.duplicate} ·{' '}
                      {t.importBankSummaryError}: {bankImportPreview.summary.error}
                    </p>
                    {bankImportPreview.summary.new === 0 ? (
                      <p className="panel-message panel-message-warning">
                        {t.importBankNoNewRows}
                      </p>
                    ) : null}
                    {selectedAccount.bankAccountNumber ? (
                      <p className="field-hint">
                        {t.bankAccountNumber}: {selectedAccount.bankAccountNumber}
                      </p>
                    ) : null}
                    {bankImportPreview.balanceReconcile.statementEndBalance !==
                    null ? (
                      <>
                        <p>
                          {t.importBankStatementEndBalance}:{' '}
                          {formatMoney(
                            bankImportPreview.balanceReconcile.statementEndBalance,
                            'JPY',
                            language
                          )}
                          {bankImportPreview.balanceReconcile.statementEndDate
                            ? ` (${bankImportPreview.balanceReconcile.statementEndDate})`
                            : null}
                        </p>
                        <p>
                          {t.importBankProjectedBalance}:{' '}
                          {formatMoney(
                            bankImportPreview.balanceReconcile
                              .projectedBalanceAfterImport,
                            'JPY',
                            language
                          )}
                        </p>
                        {bankImportPreview.balanceReconcile.reconcileDelta !==
                        0 ? (
                          <p>
                            {t.importBankReconcileDelta}:{' '}
                            {formatMoney(
                              bankImportPreview.balanceReconcile.reconcileDelta,
                              'JPY',
                              language
                            )}
                          </p>
                        ) : null}
                      </>
                    ) : null}
                    {bankImportPreview.balanceReconcile.reconcileAlreadyApplied ? (
                      <p className="panel-message">{t.importBankReconcileDone}</p>
                    ) : bankImportPreview.balanceReconcile.canApplyReconcile ? (
                      <label>
                        <input
                          checked={bankImportApplyReconcile}
                          onChange={(event) => {
                            setBankImportApplyReconcile(event.target.checked);
                            if (event.target.checked) {
                              setBankImportApplyInitial(false);
                            }
                          }}
                          type="checkbox"
                        />
                        {t.importBankReconcile}
                      </label>
                    ) : bankImportPreview.suggestedInitialBalance !== null ? (
                      <label>
                        <input
                          checked={bankImportApplyInitial}
                          onChange={(event) =>
                            setBankImportApplyInitial(event.target.checked)
                          }
                          type="checkbox"
                        />
                        {t.importBankApplyInitial}:{' '}
                        {formatMoney(
                          bankImportPreview.suggestedInitialBalance,
                          'JPY',
                          language
                        )}
                      </label>
                    ) : null}
                    {bankImportPreview.warnings.map((warning) => (
                      <p className="panel-message" key={warning.code}>
                        {warning.message}
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            <form
              className="compact-form transaction-form"
              onSubmit={createManualTransaction}
            >
              <div className="field-grid three">
                <label>
                  {t.transactionType}
                  <select
                    value={transactionForm.type}
                    onChange={(event) =>
                      setTransactionForm({
                        ...transactionForm,
                        type: event.target.value as TransactionType
                      })
                    }
                  >
                    {transactionTypes.map((value) => (
                      <option key={value} value={value}>
                        {transactionLabel(language, value)}
                      </option>
                    ))}
                  </select>
                </label>
                {transactionForm.type === 'manual_transfer' ? (
                  <>
                    <AccountSelect
                      accounts={visibleAccounts}
                      label={t.fromAccount}
                      placeholder={t.selectPlaceholder}
                      value={transactionForm.fromAccountId}
                      onChange={(value) =>
                        setTransactionForm({
                          ...transactionForm,
                          fromAccountId: value
                        })
                      }
                    />
                    <AccountSelect
                      accounts={visibleAccounts}
                      label={t.transferTarget}
                      placeholder={t.selectPlaceholder}
                      value={transactionForm.toAccountId}
                      onChange={(value) =>
                        setTransactionForm({
                          ...transactionForm,
                          toAccountId: value
                        })
                      }
                    />
                  </>
                ) : (
                  <AccountSelect
                    accounts={visibleAccounts}
                    label={t.selectAccount}
                    placeholder={t.selectPlaceholder}
                    value={transactionForm.accountId || selectedAccountId}
                    onChange={(value) =>
                      setTransactionForm({
                        ...transactionForm,
                        accountId: value
                      })
                    }
                  />
                )}
              </div>
              <div className="field-grid three">
                <label>
                  {t.amount}
                  <input
                    inputMode="numeric"
                    required
                    value={transactionForm.amount}
                    onChange={(event) =>
                      setTransactionForm({
                        ...transactionForm,
                        amount: event.target.value
                      })
                    }
                  />
                </label>
                <label>
                  {t.startDate}
                  <input
                    required
                    type="date"
                    value={transactionForm.localDate}
                    onChange={(event) =>
                      setTransactionForm({
                        ...transactionForm,
                        localDate: event.target.value
                      })
                    }
                  />
                </label>
                <label>
                  {t.note}
                  <input
                    value={transactionForm.note}
                    onChange={(event) =>
                      setTransactionForm({
                        ...transactionForm,
                        note: event.target.value
                      })
                    }
                  />
                </label>
              </div>
              <button className="primary-button" disabled={busy} type="submit">
                {t.save}
              </button>
            </form>

            <div className="history-list">
              <h3>{t.history}</h3>
              {transactions.length === 0 ? (
                <p className="panel-message">{t.noTransactions}</p>
              ) : (
                transactions.map((transaction) => (
                  <div className="history-row" key={transaction.id}>
                    <span>
                      <strong>
                        {transactionLabel(
                          language,
                          transaction.type as TransactionType
                        )}
                      </strong>
                      <small>
                        {transaction.localDate}
                        {transaction.note ? ` - ${transaction.note}` : ''}
                      </small>
                    </span>
                    <span className="history-row-side">
                      <span
                        className={
                          transaction.entry.amount < 0 ? 'negative' : 'positive'
                        }
                      >
                        {formatMoney(
                          transaction.entry.amount,
                          transaction.entry.currency,
                          language
                        )}
                      </span>
                      {canEditLedger &&
                      transaction.entry.amount < 0 &&
                      transaction.sourceType !== 'fixed_expense' ? (
                        <button
                          className="secondary-button history-row-cta"
                          disabled={busy}
                          onClick={() =>
                            openFixedExpenseFromTransaction(transaction)
                          }
                          type="button"
                        >
                          {t.registerFixedExpenseForecast}
                        </button>
                      ) : null}
                    </span>
                  </div>
                ))
              )}
            </div>
          </section>
          </section>
          ) : null}

          <AppTabNavigation
            activeTab={activeTab}
            navigationLabel={t.mainNavigation}
            onSelect={setActiveTab}
            placement="bottom"
            tabs={appTabs}
          />
        </section>
      ) : null}

    </main>
  );
}

function AppTabNavigation(props: {
  placement: 'top' | 'bottom';
  tabs: Array<{ id: AppTab; label: string; shortLabel: string }>;
  activeTab: AppTab;
  onSelect: (tab: AppTab) => void;
  navigationLabel: string;
}) {
  const placementClass =
    props.placement === 'top'
      ? 'app-tabs app-tabs--top'
      : 'app-tabs app-tabs--bottom';

  return (
    <nav className={placementClass} aria-label={props.navigationLabel}>
      {props.tabs.map((tab) => {
        const isActive = props.activeTab === tab.id;
        const displayLabel =
          props.placement === 'bottom' ? tab.shortLabel : tab.label;

        return (
          <button
            aria-current={isActive ? 'page' : undefined}
            aria-label={tab.label}
            className="app-tab"
            key={`${props.placement}-${tab.id}`}
            onClick={() => props.onSelect(tab.id)}
            type="button"
          >
            {props.placement === 'bottom' ? (
              <>
                <TabNavIcon active={isActive} tab={tab.id} />
                <span className="app-tab-label">{displayLabel}</span>
              </>
            ) : (
              displayLabel
            )}
          </button>
        );
      })}
    </nav>
  );
}

function TabNavIcon(props: { tab: AppTab; active: boolean }) {
  const stroke = props.active ? '#1f6b4f' : '#50645a';

  switch (props.tab) {
    case 'dashboard':
      return (
        <svg aria-hidden className="app-tab-icon" viewBox="0 0 24 24">
          <path
            d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z"
            fill="none"
            stroke={stroke}
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case 'accounts':
      return (
        <svg aria-hidden className="app-tab-icon" viewBox="0 0 24 24">
          <rect
            fill="none"
            height="14"
            rx="2"
            stroke={stroke}
            strokeWidth="1.8"
            width="18"
            x="3"
            y="5"
          />
          <path
            d="M3 10h18"
            fill="none"
            stroke={stroke}
            strokeWidth="1.8"
          />
        </svg>
      );
    case 'planned':
      return (
        <svg aria-hidden className="app-tab-icon" viewBox="0 0 24 24">
          <rect
            fill="none"
            height="16"
            rx="2"
            stroke={stroke}
            strokeWidth="1.8"
            width="16"
            x="4"
            y="4"
          />
          <path
            d="M8 3v3M16 3v3M4 10h16"
            fill="none"
            stroke={stroke}
            strokeLinecap="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case 'outflow':
      return (
        <svg aria-hidden className="app-tab-icon" viewBox="0 0 24 24">
          <path
            d="M12 4v12M7 11l5 5 5-5"
            fill="none"
            stroke={stroke}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
          <path
            d="M5 20h14"
            fill="none"
            stroke={stroke}
            strokeLinecap="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    case 'settings':
      return (
        <svg aria-hidden className="app-tab-icon" viewBox="0 0 24 24">
          <circle
            cx="12"
            cy="12"
            fill="none"
            r="3"
            stroke={stroke}
            strokeWidth="1.8"
          />
          <path
            d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4"
            fill="none"
            stroke={stroke}
            strokeLinecap="round"
            strokeWidth="1.8"
          />
        </svg>
      );
    default:
      return null;
  }
}

function SegmentedControl(props: {
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="segmented" aria-label={props.label}>
      {props.options.map((option) => (
        <button
          aria-pressed={props.value === option.value}
          key={option.value}
          onClick={() => props.onChange(option.value)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function RecurringRuleFields(props: {
  labels: Record<string, string>;
  value: RecurringFormState;
  onChange: (value: RecurringFormState) => void;
}) {
  const dayOfMonthDisabled = props.value.frequency !== 'monthly_day';

  return (
    <div className="field-grid three">
      <label>
        {props.labels.recurringRule}
        <select
          value={props.value.frequency}
          onChange={(event) =>
            props.onChange({
              ...props.value,
              frequency: event.target.value as RecurringFrequency
            })
          }
        >
          {recurringFrequencies.map((frequency) => (
            <option key={frequency} value={frequency}>
              {frequency === 'once'
                ? props.labels.oneTime
                : props.labels.monthlyDay}
            </option>
          ))}
        </select>
      </label>
      <label>
        {props.labels.startDate}
        <input
          required
          type="date"
          value={props.value.startDate}
          onChange={(event) =>
            props.onChange({ ...props.value, startDate: event.target.value })
          }
        />
      </label>
      <label>
        {props.labels.dayOfMonth}
        <input
          disabled={dayOfMonthDisabled}
          inputMode="numeric"
          max="31"
          min="1"
          required={!dayOfMonthDisabled}
          value={props.value.dayOfMonth}
          onChange={(event) =>
            props.onChange({ ...props.value, dayOfMonth: event.target.value })
          }
        />
      </label>
    </div>
  );
}

function PlannedList(props: {
  title: string;
  empty: string;
  children: ReactNode;
}) {
  return (
    <section className="planned-list">
      <h3>{props.title}</h3>
      {Array.isArray(props.children) && props.children.length === 0 ? (
        <p className="panel-message">{props.empty}</p>
      ) : (
        props.children
      )}
    </section>
  );
}

function AccountSelect(props: {
  accounts: Account[];
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label>
      {props.label}
      <select
        required
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      >
        <option value="">{props.placeholder}</option>
        {props.accounts.map((account) => (
          <option key={account.id} value={account.id}>
            {account.name} - {account.currency}
          </option>
        ))}
      </select>
    </label>
  );
}

function MetricCard(props: { label: string; value: string }) {
  return (
    <div className="metric-card">
      <span>{props.label}</span>
      <strong>{props.value}</strong>
    </div>
  );
}

function formatDashboardMoney(
  amount: number | undefined,
  currency: Currency,
  language: Language
) {
  return amount === undefined ? '-' : formatMoney(amount, currency, language);
}

export function isLatestDashboardRequest(
  requestSequence: number,
  latestRequestSequence: number
) {
  return requestSequence === latestRequestSequence;
}

function formatMoney(amount: number, currency: Currency, language: Language) {
  return new Intl.NumberFormat(language === 'ja' ? 'ja-JP' : 'ko-KR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(amount);
}

function recurringRulePayload(value: RecurringFormState) {
  return {
    frequency: value.frequency,
    startDate: value.startDate,
    dayOfMonth:
      value.frequency === 'monthly_day' ? toInteger(value.dayOfMonth) : null
  };
}

function toInteger(value: string): number {
  const parsed = Number(value.replace(/,/g, '').trim());
  return Number.isSafeInteger(parsed) ? parsed : Number.NaN;
}

function toNumber(value: string): number {
  const parsed = Number(value.replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function optionalNote(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
